/**
 * Operator progress report — admin UI + HTML/EML export.
 * Persists editable overlays in localStorage until backend exists.
 */

import {
  AF_KEYS,
  LG_KEYS,
  PROP_KEYS,
  buildAzulSeed,
  shouldSeedAzul,
} from "./operator-progress-seed.mjs";
import { renderOperatorSurveillanceEmbed } from "./surveillances-v2.jsx";
import {
  activeProject,
  applyImportBundles,
  buildMinutesOutlookHtml,
  buildProjectsOutlookHtml,
  chronologyCell,
  chronologyHtmlForPrint,
  createBlankProject,
  engineReasonTracker,
  ensureProjectsStore,
  formatDdMmmYy,
  isChronologyToday,
  loadSurveillanceCampaigns,
  renderProjectWidgets,
  disposeHistoryPopover,
  rtsPartsTracker,
  syncPartsFromHistory,
  syncReasonFromHistory,
} from "./operator-progress-projects.mjs";

export const OPERATOR_PROGRESS_NAV = { key: "operator-progress", label: "Operator progress", fleetScope: true };

export function isOperatorProgressRoute(key) {
  return String(key || "").trim().replace(/\/+$/, "") === "operator-progress";
}

/** Tear down live page singleton when leaving #/operator-progress. */
export function disposeOperatorProgressRoute() {
  document.querySelectorAll("body > [data-op-cell-menu]").forEach((node) => node.remove());
  document.querySelectorAll("body > [data-op-history-popover]").forEach((node) => node.remove());
  disposeHistoryPopover();
  flushActiveEditor();
  if (opProgressUnloadAbort) {
    opProgressUnloadAbort.abort();
    opProgressUnloadAbort = null;
  }
  opProgressLiveHost = null;
  opProgressLiveApi = null;
  opSurveillanceSection = null;
  opSurveillanceOperatorId = null;
}

const STORAGE_PREFIX = "actracker-operator-progress-v1:";
const DAE_RED = "#c00000";
const REPORT_TODAY_BLUE = "#1d4ed8";
const REPORT_TODAY_BG = "#dbeafe";

/** In-memory cache survives renderRoute() remounts within the same browser session. */
const sessionStoreCache = new Map();
let opProgressUnloadAbort = null;
/** Reuse one page instance while the route stays mounted — avoids remount loops on operator change. */
let opProgressLiveHost = null;
let opProgressLiveApi = null;
/** Stable operator picker DOM — options sync in place instead of full rebuild. */
let opProgressPicker = null;
/** Surveillance embed survives rerenders only while the same operator is selected. */
let opSurveillanceSection = null;
let opSurveillanceOperatorId = null;
let opSurveillanceLoadGen = 0;

const TAM_HEX = {
  "IN SERVICE": "#16a34a",
  AOG: "#991b1b",
  MAINTENANCE: "#7e22ce",
  TRANSITION: "#db2777",
  INCIDENT: "#b45309",
  STORAGE: "#374151",
  RUSSIA: "#1e3a8a",
  "CHECK WITH FM": "#0891b2",
  "CHECK WITH DM": "#0891b2",
  "AWAITING OPERATOR FEEDBACK": "#ca8a04",
  "NON-DAE": "#ea580c",
  UNKNOWN: "#111827",
};

const MONTH_MAP = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const GROUND_BUCKETS = [
  { key: "1-2w", label: "1–2 weeks", min: 0, max: 14 * 24 * 60 },
  { key: "2-4w", label: "2–4 weeks", min: 14 * 24 * 60, max: 28 * 24 * 60 },
  { key: "1-3m", label: "1–3 months", min: 28 * 24 * 60, max: 90 * 24 * 60 },
  { key: "3-6m", label: "3–6 months", min: 90 * 24 * 60, max: 180 * 24 * 60 },
  { key: "6-12m", label: "6–12 months", min: 180 * 24 * 60, max: 365 * 24 * 60 },
  { key: "1-2y", label: "1–2 years", min: 365 * 24 * 60, max: 730 * 24 * 60 },
  { key: "2y+", label: "Over 2 years", min: 730 * 24 * 60, max: Number.POSITIVE_INFINITY },
  { key: "unknown", label: "Unknown duration", min: NaN, max: NaN },
];

let el, cleanText, normaliseTam, htmlEscape, formatMaterialsExportTimeEst, tamTileClass, flightawareAircraftUrl, tamBadge;
let saveToastTimer = null;

const BADGE_BASE =
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border whitespace-nowrap";
const BADGE_VARIANTS = {
  green: `${BADGE_BASE} bg-green-50 text-green-700 border-green-200`,
  red: `${BADGE_BASE} bg-red-50 text-red-700 border-red-200`,
  purple: `${BADGE_BASE} bg-purple-50 text-purple-700 border-purple-200`,
  blue: `${BADGE_BASE} bg-blue-50 text-blue-700 border-blue-200`,
  amber: `${BADGE_BASE} bg-amber-50 text-amber-800 border-amber-200`,
  gray: `${BADGE_BASE} bg-gray-50 text-gray-700 border-gray-200`,
};

const RTS_STATUS_OPTIONS = [
  { value: "completed", label: "Completed", variant: "green", outlook: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" } },
  { value: "in_maintenance", label: "In maintenance", variant: "purple", outlook: { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff" } },
  { value: "scheduled", label: "Scheduled", variant: "blue", outlook: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" } },
  { value: "tbd", label: "TBD", variant: "red", outlook: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" } },
];

const TAM_PILL_OUTLOOK = {
  "IN SERVICE": { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  AOG: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
  MAINTENANCE: { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff" },
  TRANSITION: { bg: "#fdf2f8", color: "#be185d", border: "#fbcfe8" },
  INCIDENT: { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
  STORAGE: { bg: "#f9fafb", color: "#374151", border: "#e5e7eb" },
  RUSSIA: { bg: "#eff6ff", color: "#1e3a8a", border: "#bfdbfe" },
  "CHECK WITH FM": { bg: "#ecfeff", color: "#0891b2", border: "#a5f3fc" },
  "CHECK WITH DM": { bg: "#ecfeff", color: "#0891b2", border: "#a5f3fc" },
  "AWAITING OPERATOR FEEDBACK": { bg: "#fefce8", color: "#ca8a04", border: "#fef08a" },
  "NON-DAE": { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
  UNKNOWN: { bg: "#f9fafb", color: "#111827", border: "#e5e7eb" },
};

function progressBadge(label, variant = "gray") {
  return el("span", { class: BADGE_VARIANTS[variant] || BADGE_VARIANTS.gray, text: label || "—" });
}

function normaliseRtsStatus(v) {
  const s = String(v || "scheduled").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (s === "completed" || s === "complete") return "completed";
  if (s === "in_maintenance" || s === "maintenance") return "in_maintenance";
  if (s === "tbd" || s === "to_be_determined" || s === "to_be_decided") return "tbd";
  return "scheduled";
}

function rtsStatusMeta(v) {
  const key = normaliseRtsStatus(v);
  return RTS_STATUS_OPTIONS.find((o) => o.value === key) || RTS_STATUS_OPTIONS[2];
}

function rtsStatusBadge(v) {
  const meta = rtsStatusMeta(v);
  return progressBadge(meta.label, meta.variant);
}

function engineStatusBadge(status) {
  const u = String(status || "").toUpperCase();
  if (u === "SERVICEABLE") return progressBadge("Serviceable", "green");
  if (u === "UNSERVICEABLE") return progressBadge("Unserviceable", "red");
  return progressBadge(status || "—", "gray");
}

function engineDispositionBadge(disp) {
  const d = String(disp || "").toLowerCase();
  if (d === "in_shop") return progressBadge("In shop", "amber");
  if (d === "in_storage") return progressBadge("In storage", "gray");
  return null;
}

function formatModelScopeLabel(families) {
  const list = (families || []).map((f) => cleanText(f)).filter(Boolean);
  return list.length ? list.join(", ") : "All types";
}

/** Compact report meta line: WK 26-2026 - 23-JUN-2026 20:39 EST - 26 aircraft - ATR 72 */
function formatReportHeaderLine(weekMeta, date, total, modelFamilies) {
  const d = date instanceof Date ? date : new Date(date);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const day = parts.find((p) => p.type === "day")?.value || "01";
  const month = String(parts.find((p) => p.type === "month")?.value || "Jan").toUpperCase();
  const year = parts.find((p) => p.type === "year")?.value || "";
  const hour = parts.find((p) => p.type === "hour")?.value || "00";
  const minute = parts.find((p) => p.type === "minute")?.value || "00";
  const scope = formatModelScopeLabel(modelFamilies);
  return `WK ${weekMeta.week}-${weekMeta.year} - ${day}-${month}-${year} ${hour}:${minute} EST - ${total} aircraft - ${scope}`;
}

function countRowsByFamily(matched, families) {
  const counts = Object.fromEntries(families.map((f) => [f, 0]));
  for (const r of matched) {
    const fam = cleanText(r.family);
    if (counts[fam] !== undefined) counts[fam] += 1;
  }
  return counts;
}

function outlookPill(text, { bg = "#f9fafb", color = "#374151", border = "#e5e7eb" } = {}) {
  return `<span style="display:inline-block;padding:2px 10px;border-radius:999px;font-size:11px;font-weight:600;background:${bg};color:${color};border:1px solid ${border};white-space:nowrap;">${htmlEscape(text)}</span>`;
}

function outlookTamPill(tam) {
  const t = normaliseTam(tam);
  const c = TAM_PILL_OUTLOOK[t] || TAM_PILL_OUTLOOK.UNKNOWN;
  return outlookPill(t, c);
}

function outlookRtsPill(v) {
  const meta = rtsStatusMeta(v);
  return outlookPill(meta.label, meta.outlook);
}

function outlookEngineStatusPill(status) {
  const u = String(status || "").toUpperCase();
  if (u === "SERVICEABLE") return outlookPill("Serviceable", { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" });
  if (u === "UNSERVICEABLE") return outlookPill("Unserviceable", { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" });
  return outlookPill(status || "—");
}

function outlookDispositionPill(disp) {
  const d = String(disp || "").toLowerCase();
  if (d === "in_shop") return outlookPill("In shop", { bg: "#fffbeb", color: "#b45309", border: "#fde68a" });
  if (d === "in_storage") return outlookPill("In storage", { bg: "#f9fafb", color: "#374151", border: "#e5e7eb" });
  return "";
}

function ensureVisibleWidgets(store) {
  if (!store || typeof store !== "object") return {};
  if (!store.visibleWidgets || typeof store.visibleWidgets !== "object") {
    store.visibleWidgets = Object.fromEntries(EXPORT_SECTIONS.map((s) => [s.id, s.default === true]));
  }
  for (const sec of EXPORT_SECTIONS) {
    if (store.visibleWidgets[sec.id] === undefined) store.visibleWidgets[sec.id] = sec.default === true;
  }
  // One-shot: surface minutes when data exists — never again after the user configures widgets.
  const hasMinuteRows = (store.projects || []).some(
    (p) => Array.isArray(p?.minutes) && p.minutes.length > 0,
  );
  if (
    hasMinuteRows
    && store._autoEnabledMinutes !== true
    && store._widgetsUserConfigured !== true
  ) {
    store.visibleWidgets.minutes = true;
    store.visibleWidgets.projects = true;
    store._autoEnabledMinutes = true;
    store._pendingAutoEnablePersist = true;
  }
  return store.visibleWidgets;
}

function migrateStore(data) {
  if (!data || typeof data !== "object") return data;
  if (Array.isArray(data.mroPipeline)) {
    for (const row of data.mroPipeline) {
      if (!row.rtsStatus) row.rtsStatus = "scheduled";
      if (!row._rowId) row._rowId = `rts-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      row.mtxStart = normalizeMtxDate(row.mtxStart);
      row.mtxComplete = normalizeMtxDate(row.mtxComplete);
    }
  }
  if (Array.isArray(data.engineTracker)) {
    for (const row of data.engineTracker) {
      if (!row._rowId) row._rowId = `eng-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      normalizeEngineTarget(row);
    }
  }
  if (Array.isArray(data.serviceability)) {
    for (const row of data.serviceability) {
      if (!row._rowId) row._rowId = `svc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    }
  }
  ensureVisibleWidgets(data);
  return ensureProjectsStore(data);
}

function svcGroupBorder(position) {
  const map = {
    start: "border-l-2 border-r border-gray-300",
    mid: "border-r border-gray-300",
    end: "border-r-2 border-gray-300",
    single: "border-x-2 border-gray-300",
  };
  return map[position] || "";
}

function storageKey(operator, operators = null) {
  const idKey = legacyIdStorageKey(operator, operators);
  if (idKey) return idKey;
  const name = cleanText(operator).toLowerCase();
  return `${STORAGE_PREFIX}${name}`;
}

function legacyIdStorageKey(operator, operators = null) {
  const name = cleanText(operator).toLowerCase();
  const match = (operators || []).find((o) => cleanText(o?.name).toLowerCase() === name);
  if (match?.id != null && match.id !== "") return `${STORAGE_PREFIX}id:${match.id}`;
  return null;
}

function allStorageKeysForOperator(operator, operators = null) {
  const keys = [storageKey(operator, operators)];
  const idKey = legacyIdStorageKey(operator, operators);
  if (idKey && !keys.includes(idKey)) keys.push(idKey);
  return keys;
}

function storeTimestamp(data) {
  if (!data || typeof data !== "object") return 0;
  const serverAt = Date.parse(data._serverUpdatedAt || "");
  const localAt = Date.parse(data._lastSavedAt || "");
  const best = Math.max(
    Number.isFinite(serverAt) ? serverAt : 0,
    Number.isFinite(localAt) ? localAt : 0,
  );
  if (best > 0) return best;
  const engines = Array.isArray(data.engineTracker) ? data.engineTracker.length : 0;
  const svc = Array.isArray(data.serviceability) ? data.serviceability.length : 0;
  return engines + svc;
}

function storeRichness(data) {
  if (!data || typeof data !== "object") return 0;
  const projects = Array.isArray(data.projects) ? data.projects : [];
  const minutes = projects.reduce((n, p) => n + (p.minutes?.length || 0), 0);
  const tasks = projects.reduce((n, p) => n + (p.tasks?.length || 0), 0);
  return (data.engineTracker?.length || 0)
    + (data.serviceability?.length || 0)
    + (data.mroPipeline?.length || 0)
    + projects.length * 10
    + minutes
    + tasks * 3;
}

function allLocalStorageKeysForOperator(operator, operators = null) {
  const keys = new Set(allStorageKeysForOperator(operator, operators));
  const compare = operatorCompareKeyClient(operator);
  for (const op of operators || []) {
    if (operatorCompareKeyClient(op?.name) !== compare) continue;
    const n = cleanText(op?.name).toLowerCase();
    if (n) keys.add(`${STORAGE_PREFIX}${n}`);
    const id = Number(op?.id);
    if (Number.isFinite(id) && id > 0) keys.add(`${STORAGE_PREFIX}id:${id}`);
  }
  const n = cleanText(operator).toLowerCase();
  if (n) keys.add(`${STORAGE_PREFIX}${n}`);
  return [...keys];
}

function writeStoreToAllLocalKeys(operator, data, operators = null, { touch = true } = {}) {
  const payload = JSON.stringify(data);
  for (const key of allLocalStorageKeysForOperator(operator, operators)) {
    try {
      localStorage.setItem(key, payload);
    } catch { /* ignore quota */ }
  }
  sessionStoreCache.set(storageKey(operator, operators), data);
}

function hasUnsyncedLocalEdits(data) {
  if (!data || typeof data !== "object") return false;
  const editAt = Date.parse(data._lastSavedAt || "");
  const syncAt = Date.parse(data._serverUpdatedAt || "");
  if (!Number.isFinite(editAt)) return false;
  if (!Number.isFinite(syncAt)) return false;
  return editAt > syncAt + 2000;
}

function pickAuthoritativeStore(local, server) {
  if (!server) return local;
  if (!local) return server;

  const serverAt = Date.parse(server._serverUpdatedAt || "");
  const localSyncAt = Date.parse(local._serverUpdatedAt || "");
  const localRich = storeRichness(local);
  const serverRich = storeRichness(server);

  // This browser has edits never confirmed by the server — keep local and push upstream.
  if (hasUnsyncedLocalEdits(local) && localRich >= serverRich - 2) return local;

  // Server updated since this browser last synced — server is truth for all devices.
  let chosen = null;
  if (Number.isFinite(serverAt) && (!Number.isFinite(localSyncAt) || serverAt > localSyncAt + 1000)) {
    chosen = server;
  } else if (serverRich > localRich + 3) {
    chosen = server;
  } else if (localRich > serverRich + 3 && hasUnsyncedLocalEdits(local)) {
    chosen = local;
  } else {
    chosen = pickNewerStore(local, server);
  }

  // Keep this browser's widget toggles if the user configured them more recently than the server snapshot.
  if (
    chosen === server
    && local?._widgetsUserConfigured === true
    && local.visibleWidgets
    && typeof local.visibleWidgets === "object"
  ) {
    const localEdit = Date.parse(local._lastSavedAt || "");
    if (Number.isFinite(localEdit) && (!Number.isFinite(serverAt) || localEdit >= serverAt - 2000)) {
      chosen = {
        ...server,
        visibleWidgets: { ...local.visibleWidgets },
        _widgetsUserConfigured: true,
        _autoEnabledMinutes: local._autoEnabledMinutes ?? server._autoEnabledMinutes,
      };
    }
  }
  return chosen;
}

function pickNewerStore(a, b) {
  if (!a) return b;
  if (!b) return a;
  const at = storeTimestamp(a);
  const bt = storeTimestamp(b);
  if (bt > at) return b;
  if (at > bt) return a;
  return a;
}

function readStoreFromKey(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return migrateStore(parsed);
  } catch {
    return null;
  }
}

function operatorCompareKeyClient(value) {
  let x = cleanText(value)
    .toLowerCase()
    .replace(/\b(company|limited|ltd|inc|s\.?a\.?|group|airlines|airline)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!x) return "";
  if (x === "vivaaerobus" || x === "viva aerobus") return "viva aerobus";
  return x;
}

function operatorIdForName(operator, operators = null) {
  const exact = cleanText(operator).toLowerCase();
  const key = operatorCompareKeyClient(operator);
  const match = (operators || []).find((o) => {
    const n = cleanText(o?.name).toLowerCase();
    return n === exact || (key && operatorCompareKeyClient(o?.name) === key);
  });
  const id = Number(match?.id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function aircraftMatchesOperator(a, operator, operators = null) {
  const opKey = cleanText(operator).toLowerCase();
  if (!opKey) return false;
  const acOp = cleanText(a?.operator).toLowerCase();
  if (acOp === opKey) return true;
  if (acOp && (acOp.includes(opKey) || opKey.includes(acOp))) return true;
  const opId = operatorIdForName(operator, operators);
  if (opId != null && Number(a?.operator_id ?? a?.operatorId) === opId) return true;
  return false;
}

function loadStoreFromDisk(operator, operators = null, { allowSeed = true } = {}) {
  const primaryKey = storageKey(operator, operators);
  let best = null;
  for (const key of allStorageKeysForOperator(operator, operators)) {
    const data = readStoreFromKey(key);
    if (data) best = pickNewerStore(best, data);
  }

  if (best) {
    applyImportBundles(best, operator);
    try {
      localStorage.setItem(primaryKey, JSON.stringify(best));
      const idKey = legacyIdStorageKey(operator, operators);
      if (idKey && idKey !== primaryKey) localStorage.setItem(idKey, JSON.stringify(best));
    } catch { /* ignore */ }
    return best;
  }

  if (allowSeed && shouldSeedAzul(operator) && operators != null) {
    const seeded = migrateStore(buildAzulSeed());
    applyImportBundles(seeded, operator);
    try {
      localStorage.setItem(primaryKey, JSON.stringify(seeded));
    } catch { /* ignore */ }
    return seeded;
  }
  const empty = migrateStore({ version: 2, mroPipeline: [], serviceability: [], engineTracker: [], projects: [] });
  applyImportBundles(empty, operator);
  return empty;
}

function acquireStore(operator, operators = null) {
  const key = storageKey(operator, operators);
  if (sessionStoreCache.has(key)) return sessionStoreCache.get(key);
  const data = loadStoreFromDisk(operator, operators, { allowSeed: operators != null });
  sessionStoreCache.set(key, data);
  return data;
}

function loadStore(operator, operators = null) {
  return acquireStore(operator, operators);
}

function flushActiveEditor() {
  const active = document.activeElement;
  if (!active) return;
  const tag = String(active.tagName || "").toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
    active.blur();
  }
}

function saveStore(operator, data, operators = null, { touch = true } = {}) {
  if (touch) data._lastSavedAt = new Date().toISOString();
  try {
    writeStoreToAllLocalKeys(operator, data, operators, { touch: false });
  } catch (e) {
    window.alert(`Unable to save operator progress: ${e?.message || e}`);
  }
}

function isRetriableServerError(message) {
  return /502|503|504|bad gateway|service unavailable|gateway timeout|ECONNREFUSED/i.test(String(message || ""));
}

async function sleepMs(ms) {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

async function loadStoreFromServer(operatorId, fetchJson, apiUrl, { retries = 3 } = {}) {
  if (!operatorId || !fetchJson || !apiUrl) return null;
  let lastErr = null;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const resp = await fetchJson(apiUrl(`/api/operator-progress/${operatorId}`));
      if (!resp?.ok) {
        lastErr = new Error(cleanText(resp?.error) || "Server returned an error");
        if (!isRetriableServerError(lastErr.message) || attempt >= retries - 1) {
          return { error: lastErr.message };
        }
        await sleepMs(700 * (attempt + 1));
        continue;
      }
      if (!resp.data) return null;
      const data = migrateStore(resp.data);
      if (resp.updatedAt) data._serverUpdatedAt = resp.updatedAt;
      return data;
    } catch (e) {
      lastErr = e;
      if (!isRetriableServerError(e?.message) || attempt >= retries - 1) {
        return { error: cleanText(e?.message) || String(e) };
      }
      await sleepMs(700 * (attempt + 1));
    }
  }
  return { error: cleanText(lastErr?.message) || "Server unavailable" };
}

async function saveStoreToServer(operatorId, data, fetchJson, apiUrl, { retries = 3 } = {}) {
  if (!operatorId || !fetchJson || !apiUrl) return { ok: false, error: "No operator id" };
  let lastErr = null;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const resp = await fetchJson(apiUrl(`/api/operator-progress/${operatorId}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data }),
      });
      return resp || { ok: false };
    } catch (e) {
      lastErr = e;
      if (!isRetriableServerError(e?.message) || attempt >= retries - 1) {
        return { ok: false, error: e?.message || String(e) };
      }
      await sleepMs(800 * (attempt + 1));
    }
  }
  return { ok: false, error: cleanText(lastErr?.message) || "Server unavailable" };
}

function showSaveToast(message = "Saved") {
  let toast = document.querySelector("[data-op-save-toast]");
  if (!toast) {
    toast = el("div", {
      "data-op-save-toast": "1",
      class:
        "fixed bottom-4 right-4 z-[60] px-4 py-2 bg-green-700 text-white text-sm rounded-lg shadow-lg transition-opacity duration-300 opacity-0 pointer-events-none",
      text: message,
    });
    document.body.appendChild(toast);
  } else {
    toast.textContent = message;
  }
  toast.classList.remove("opacity-0");
  toast.classList.add("opacity-100");
  clearTimeout(saveToastTimer);
  saveToastTimer = setTimeout(() => {
    toast.classList.remove("opacity-100");
    toast.classList.add("opacity-0");
  }, 2200);
}

function parseLooseDate(value) {
  const t = cleanText(value);
  if (!t) return null;
  const direct = Date.parse(t);
  if (Number.isFinite(direct)) return direct;
  const m = t.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
  if (m) {
    const day = Number(m[1]);
    const mon = MONTH_MAP[m[2].toLowerCase()];
    let year = Number(m[3]);
    if (year < 100) year += 2000;
    if (mon == null || !Number.isFinite(day) || !Number.isFinite(year)) return null;
    return Date.UTC(year, mon, day);
  }
  const mShort = t.match(/^(\d{1,2})-([A-Za-z]{3})$/);
  if (mShort) {
    const day = Number(mShort[1]);
    const mon = MONTH_MAP[mShort[2].toLowerCase()];
    if (mon == null || !Number.isFinite(day)) return null;
    return Date.UTC(new Date().getUTCFullYear(), mon, day);
  }
  return null;
}

function computeTatDays(mtxStart, mtxComplete) {
  const a = parseLooseDate(mtxStart);
  const b = parseLooseDate(mtxComplete);
  if (a == null || b == null) return "";
  const days = Math.round((b - a) / 86400000);
  return days >= 0 ? String(days) : "";
}

function fmtDateDdMmmYy(value, { empty = "—" } = {}) {
  const raw = cleanText(value);
  if (!raw) return empty;
  const ms = typeof value === "number" ? value : parseLooseDate(value);
  if (ms == null || !Number.isFinite(ms)) return raw || empty;
  const dt = new Date(ms);
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  const mmm = MONTH_ABBR[dt.getUTCMonth()] || "—";
  const yy = String(dt.getUTCFullYear()).slice(-2);
  return `${dd}-${mmm}-${yy}`;
}

function mtxDateToIso(value) {
  const ms = parseLooseDate(value);
  if (ms == null) return "";
  return formatIsoDateUtc(ms);
}

function mtxDateFromIso(iso) {
  const t = cleanText(iso);
  if (!t) return "";
  const ms = Date.parse(`${t}T00:00:00Z`);
  if (!Number.isFinite(ms)) return t;
  return fmtDateDdMmmYy(ms);
}

function normalizeMtxDate(value) {
  const t = cleanText(value);
  if (!t) return "";
  const ms = parseLooseDate(t);
  if (ms == null) return t;
  return fmtDateDdMmmYy(ms);
}

function stampRowChangedToday(row) {
  if (row && typeof row === "object") row._changedAt = new Date().toISOString();
}

function isTimestampToday(value, now = new Date()) {
  if (value == null || value === "") return false;
  const ms = Date.parse(value);
  if (Number.isFinite(ms)) {
    const dt = new Date(ms);
    return dt.getFullYear() === now.getFullYear() && dt.getMonth() === now.getMonth() && dt.getDate() === now.getDate();
  }
  return isChronologyToday(value, now);
}

function todayDateLabel(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return formatDdMmmYy(`${y}-${m}-${d}`);
}

function rowUpdatedToday(row, textKeys = []) {
  if (!row || typeof row !== "object") return false;
  if (isTimestampToday(row._changedAt)) return true;
  const label = todayDateLabel();
  if (!label) return false;
  for (const key of textKeys) {
    if (String(row[key] || "").includes(label)) return true;
  }
  return false;
}

function reportTd(innerHtml, { today = false, extra = "" } = {}) {
  const bg = today ? REPORT_TODAY_BG : "#ffffff";
  const color = today ? REPORT_TODAY_BLUE : "#1e1f24";
  const weight = today ? "font-weight:bold;" : "";
  return `<td style="border:1px solid #d8dbe0;padding:6px 8px;font-family:Calibri,Arial,sans-serif;font-size:12px;vertical-align:top;background:${bg};color:${color};${weight}${extra}">${innerHtml}</td>`;
}

function dubaiYmd(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Dubai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  return {
    y: Number(parts.find((p) => p.type === "year")?.value),
    m: Number(parts.find((p) => p.type === "month")?.value),
    d: Number(parts.find((p) => p.type === "day")?.value),
  };
}

function isoWeekYearFromYmd(y, m, d) {
  const dt = new Date(Date.UTC(y, m - 1, d));
  const day = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((dt - yearStart) / 86400000 + 1) / 7);
  return { week, year: dt.getUTCFullYear() };
}

function reportWeekMeta(date = new Date()) {
  const { y, m, d } = dubaiYmd(date);
  return isoWeekYearFromYmd(y, m, d);
}

function reportSubject(operator, date = new Date()) {
  const { week, year } = reportWeekMeta(date);
  return `${String(operator || "OPERATOR").trim().toUpperCase()} Progress WK${week}-${year}`;
}

function pct(count, total) {
  if (!total) return "0.0%";
  return `${((count / total) * 100).toFixed(1)}%`;
}

function normaliseEngineStatus(v) {
  const s = String(v || "S").trim().toUpperCase();
  if (s === "U") return "U";
  return "S";
}

const ENGINE_TARGET_MODES = [
  { value: "done", label: "Done" },
  { value: "tbd", label: "TBD" },
  { value: "date", label: "Date" },
];

const EXPORT_SECTIONS = [
  { id: "fleetSummary", label: "A · Fleet summary", default: true, emailDefault: true },
  { id: "enginesSummary", label: "B · Engines summary", default: true, emailDefault: true },
  { id: "rts", label: "C · Return to service plan", default: true, emailDefault: true },
  { id: "ground", label: "D · Time on ground (not in service)", default: false, emailDefault: false },
  { id: "serviceability", label: "E · Serviceability table", default: false, emailDefault: false },
  { id: "engineTracker", label: "F · Engine tracker", default: true, emailDefault: true },
  { id: "fleetSnapshot", label: "G · Full fleet snapshot", default: false, emailDefault: false },
  { id: "heatmap", label: "H · Fleet heatmap", default: false, emailDefault: false },
  { id: "surveillances", label: "I · Surveillances", default: false, emailDefault: false },
  { id: "projects", label: "Projects & tracking", default: false, emailDefault: false },
  { id: "projectSummary", label: "J · Project summary (with schedule)", default: false, emailDefault: false },
  { id: "contacts", label: "K · Contacts", default: false, emailDefault: false },
  { id: "minutes", label: "L · Meeting minutes", default: false, emailDefault: true },
  { id: "tasks", label: "M · Task schedule", default: false, emailDefault: false },
  { id: "attachments", label: "N · Attachments & documents", default: false, emailDefault: false },
  { id: "azulTracker", label: "Azul progress tracker", default: false, emailDefault: false },
];

/** Default checked sections for Preview / .eml / HTML export: A B C F L. */
function defaultEmailExportFlags() {
  return Object.fromEntries(
    EXPORT_SECTIONS.map((s) => [s.id, s.emailDefault === true]),
  );
}

/** Project chrome + J–N / tracker — any one on is enough to show the projects panel. */
const PROJECT_WIDGET_IDS = ["projects", "projectSummary", "contacts", "minutes", "tasks", "attachments", "azulTracker"];
const PROJECT_SECTION_WIDGET_IDS = ["projectSummary", "contacts", "minutes", "tasks", "attachments", "azulTracker"];

function anyProjectWidgetOn(widgets) {
  return PROJECT_WIDGET_IDS.some((id) => widgets?.[id] === true);
}

function anyProjectSectionOn(flags) {
  return PROJECT_SECTION_WIDGET_IDS.some((id) => flags?.[id] === true);
}

const SECTION_TITLE_CLASS = "text-sm font-bold text-dae uppercase tracking-wide";
const RTS_FILING_SVC_HEADER = "A/C serviceable\nat time of filing";
const ENGINES_UNSERVICEABLE_FILING_NOTE = "Unserviceable engines at time of filing";

function sectionHeading(label, { subtitle = null, className = SECTION_TITLE_CLASS } = {}) {
  const nodes = [el("h2", { class: `${className} mb-2`, text: label })];
  if (subtitle) nodes.push(el("p", { class: "text-xs text-gray-500 -mt-1 mb-2", text: subtitle }));
  return el("div", {}, nodes);
}

function outlookSectionTitle(label) {
  return `<div style="font-size:14px;font-weight:bold;color:${DAE_RED};margin:20px 0 8px;text-transform:uppercase;">${htmlEscape(label)}</div>`;
}

function exportSectionLabel(id) {
  return EXPORT_SECTIONS.find((s) => s.id === id)?.label || id;
}

function resolveStoreRow(list, row, { idKey = "_rowId", matchKeys = [] } = {}) {
  if (!row || !Array.isArray(list)) return row;
  if (list.includes(row)) return row;
  const rowId = row[idKey];
  if (rowId) {
    const byId = list.find((r) => r[idKey] === rowId);
    if (byId) return byId;
  }
  for (const keys of matchKeys) {
    const keyList = Array.isArray(keys) ? keys : [keys];
    const parts = keyList.map((k) => cleanText(row[k]));
    if (!parts.some(Boolean)) continue;
    const hit = list.find((r) => keyList.every((k, i) => cleanText(r[k]) === parts[i]));
    if (hit) return hit;
  }
  return row;
}

function formatIsoDateUtc(ms) {
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatTargetDateDisplay(value) {
  const ms = parseLooseDate(value) ?? Date.parse(value || "");
  if (!Number.isFinite(ms)) return cleanText(value) || "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(ms));
  } catch {
    return cleanText(value) || "—";
  }
}

function normalizeEngineTarget(row) {
  if (!row || typeof row !== "object") return row;
  if (row.targetMode === "done" || row.targetMode === "tbd" || row.targetMode === "date") {
    row.target = formatEngineTargetDisplay(row);
    return row;
  }
  const t = cleanText(row.target);
  const upper = t.toUpperCase();
  if (upper === "DONE") {
    row.targetMode = "done";
    row.targetDate = "";
  } else if (upper === "TBD" || !t) {
    row.targetMode = "tbd";
    row.targetDate = "";
  } else {
    const ms = parseLooseDate(t);
    row.targetMode = "date";
    row.targetDate = ms != null ? formatIsoDateUtc(ms) : "";
    if (!row.targetDate) row.targetLegacy = t;
  }
  row.target = formatEngineTargetDisplay(row);
  return row;
}

function formatEngineTargetDisplay(row) {
  const mode = row?.targetMode || "tbd";
  if (mode === "done") return "Done";
  if (mode === "tbd") return "TBD";
  if (mode === "date") {
    if (row.targetDate) return formatTargetDateDisplay(row.targetDate);
    if (row.targetLegacy) return row.targetLegacy;
    return "—";
  }
  return cleanText(row?.target) || "TBD";
}

function engineTargetSortKey(row) {
  normalizeEngineTarget(row);
  if (row.targetMode === "date") {
    const ms = row.targetDate ? parseLooseDate(row.targetDate) ?? Date.parse(row.targetDate) : parseLooseDate(row.targetLegacy);
    if (Number.isFinite(ms)) return ms;
    return Number.POSITIVE_INFINITY - 2;
  }
  if (row.targetMode === "tbd") return Number.POSITIVE_INFINITY - 1;
  if (row.targetMode === "done") return Number.POSITIVE_INFINITY;
  return Number.POSITIVE_INFINITY - 3;
}

function compareSortValues(a, b, dir = "asc") {
  const mult = dir === "desc" ? -1 : 1;
  if (a === b) return 0;
  if (a == null || a === "") return 1;
  if (b == null || b === "") return -1;
  if (typeof a === "number" && typeof b === "number") return (a - b) * mult;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" }) * mult;
}

function applyTableSort(items, sortState, getSortValue) {
  if (!sortState?.key || !Array.isArray(items)) return items;
  return [...items].sort((a, b) => compareSortValues(getSortValue(a), getSortValue(b), sortState.dir));
}

function sortableTh(label, colKey, sortState, onSort, className = "", extra = {}) {
  const active = sortState?.key === colKey;
  const arrow = active ? (sortState.dir === "asc" ? " ▲" : " ▼") : "";
  const lines = String(label).split("\n").filter(Boolean);
  const labelContent = lines.length > 1
    ? el("span", { class: `block leading-tight normal-case whitespace-normal text-[10px] ${extra.narrow ? "max-w-[3.25rem]" : ""}` },
        lines.map((line, i) => el("span", { class: i > 0 ? "block" : "", text: line + (i === lines.length - 1 ? arrow : "") }))
      )
    : null;
  return el("th", {
    class: `px-2 py-2 font-medium cursor-pointer select-none hover:bg-red-700 ${extra.narrow ? "max-w-[3.25rem]" : ""} ${className}`,
    ...(labelContent ? { children: [labelContent] } : { text: `${label}${arrow}` }),
    rowspan: extra.rowspan || undefined,
    colspan: extra.colspan || undefined,
    onclick: () => {
      if (sortState.key === colKey) sortState.dir = sortState.dir === "asc" ? "desc" : "asc";
      else {
        sortState.key = colKey;
        // MTX dates: first click uses earliest-first; toggle for reverse.
        sortState.dir = "asc";
      }
      onSort();
    },
  });
}

function engineSortValue(row, key) {
  normalizeEngineTarget(row);
  if (key === "esn") return row.esn;
  if (key === "status") return String(row.azulStatus || "");
  if (key === "disposition") return String(row.disposition || "");
  if (key === "location") return row.location;
  if (key === "target") return engineTargetSortKey(row);
  if (key === "cost") {
    const n = parseEngineCost(row.cost);
    return n != null ? n : "";
  }
  if (key === "reason") return row.reason;
  return "";
}

function parseEngineCost(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const n = Number(raw.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function formatEngineCost(value) {
  const n = parseEngineCost(value);
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function normalizeEngineCostStorage(value) {
  const n = parseEngineCost(value);
  return n == null ? "" : String(n);
}

function sumEngineTrackerCost(engineTracker) {
  return (engineTracker || []).reduce((sum, row) => {
    const n = parseEngineCost(row.cost);
    return n != null ? sum + n : sum;
  }, 0);
}

function rtsMtxDateMs(row, field) {
  const ms = parseLooseDate(row?.[field]);
  return Number.isFinite(ms) ? ms : null;
}

/** Empty / TBD MTX dates always sink to the bottom; dated rows follow dir.
 * Default: MTX start earliest-first (2026 before 2027), TBD last.
 */
function compareRtsMtxDate(a, b, field, dir = "asc") {
  const aMs = rtsMtxDateMs(a, field);
  const bMs = rtsMtxDateMs(b, field);
  if (aMs == null && bMs == null) {
    const aTbd = normaliseRtsStatus(a?.rtsStatus) === "tbd";
    const bTbd = normaliseRtsStatus(b?.rtsStatus) === "tbd";
    if (aTbd !== bTbd) return aTbd ? 1 : -1;
    return 0;
  }
  if (aMs == null) return 1;
  if (bMs == null) return -1;
  return dir === "desc" ? bMs - aMs : aMs - bMs;
}

function rtsSortValue(row, key) {
  if (key === "idx") return row._idx;
  if (key === "msn") return row.msn;
  if (key === "reg") return row.reg;
  if (key === "mtxRequirement") return row.mtxRequirement;
  if (key === "rtsStatus") return row.rtsStatus;
  if (key === "mtxStart") return rtsMtxDateMs(row, "mtxStart");
  if (key === "mtxComplete") return rtsMtxDateMs(row, "mtxComplete");
  if (key === "tatDays") return Number(row.tatDays) || "";
  if (key === "mro") return row.mro;
  if (key === "serviceabilityTable") return row.serviceabilityTable;
  if (key === "parts") return row.parts;
  return "";
}

function sortRtsRows(rows, sortState) {
  const rtsSort = sortState?.key ? sortState : { key: "mtxStart", dir: "asc" };
  const key = rtsSort.key || "mtxStart";
  const dir = rtsSort.dir || "asc";
  if (key === "mtxStart" || key === "mtxComplete") {
    return [...(rows || [])].sort((a, b) => compareRtsMtxDate(a, b, key, dir));
  }
  return applyTableSort(rows || [], { key, dir }, (r) => rtsSortValue(r, key));
}

function serviceabilitySortValue(row, key) {
  if (key === "msn") return row.msn;
  if (key === "comments") return row.comments;
  if (key === "eng1esn") return row.eng1?.esn;
  if (key === "eng2esn") return row.eng2?.esn;
  return row.msn;
}

function fleetRowSortValue(row, key) {
  if (key === "msn") return row.msn;
  if (key === "reg") return row.reg;
  if (key === "model") return row.model;
  if (key === "tam") return normaliseTam(row.tam);
  if (key === "tog") return Number.isFinite(row.togMinutes) ? row.togMinutes : row.tog;
  if (key === "loc") return row.loc;
  return row.msn;
}

function groundRowSortValue(row, key, ground) {
  if (key === "count") return Number(row.count) || 0;
  if (ground?.multiFamily && ground.families?.includes(key)) return Number(row.familyCounts?.[key]) || 0;
  if (key === "label") {
    if (row.isTotal) return 9999;
    if (row.label === "In service") return -1;
    const idx = GROUND_BUCKETS.findIndex((b) => b.label === row.label);
    return idx >= 0 ? idx : 500;
  }
  return row.label;
}

function enrichReportSorting(reportCtx, store, sortState) {
  const engines = (store.engineTracker || []).map((e) => { normalizeEngineTarget(e); return e; });
  reportCtx.sortedEngines = sortState?.engine?.key
    ? applyTableSort(engines, sortState.engine, (r) => engineSortValue(r, sortState.engine.key))
    : sortEngineTracker(engines);

  const mro = (store.mroPipeline || []).map((r, i) => ({ ...r, _idx: i + 1 }));
  // Reports (.eml / HTML): MTX start newest-first, empty/TBD last (ignore on-screen column clicks).
  reportCtx.sortedMro = sortRtsRows(mro, { key: "mtxStart", dir: "asc" }).map((r, i) => ({
    ...r,
    _idx: i + 1,
  }));

  const svc = [...(store.serviceability || [])];
  reportCtx.sortedServiceability = sortState?.serviceability?.key
    ? applyTableSort(svc, sortState.serviceability, (r) => serviceabilitySortValue(r, sortState.serviceability.key))
    : svc;

  const fleet = [...(reportCtx.fleetRows || [])];
  reportCtx.sortedFleetRows = sortState?.fleet?.key
    ? applyTableSort(fleet, sortState.fleet, (r) => fleetRowSortValue(r, sortState.fleet.key))
    : fleet;

  const groundRows = [...(reportCtx.ground?.rows || [])];
  const groundTotal = reportCtx.ground?.totalRow;
  let sortedGround = sortState?.ground?.key
    ? applyTableSort(groundRows, sortState.ground, (r) => groundRowSortValue(r, sortState.ground.key, reportCtx.ground))
    : groundRows;
  reportCtx.groundSorted = {
    ...reportCtx.ground,
    rows: sortedGround,
    totalRow: groundTotal,
  };
}

function engineTargetEditCell(row, onSave, resolveRow = (r) => r) {
  const target = () => resolveRow(row);
  normalizeEngineTarget(target());
  let editing = false;
  const td = el("td", { class: "px-2 py-1 align-top min-w-[8.5rem]" });
  const staticEl = el("span", {
    class: "block min-h-[1.25rem] whitespace-pre-wrap cursor-text rounded px-1 -mx-1 hover:bg-red-50 text-xs text-gray-800",
  });

  const refreshStatic = () => {
    const t = target();
    normalizeEngineTarget(t);
    staticEl.textContent = formatEngineTargetDisplay(t);
  };
  refreshStatic();

  const targetDateIso = () => {
    const t = target();
    if (t.targetDate && /^\d{4}-\d{2}-\d{2}$/.test(t.targetDate)) return t.targetDate;
    const ms = parseLooseDate(t.targetLegacy || t.targetDate);
    return ms != null ? formatIsoDateUtc(ms) : "";
  };

  const openEditor = () => {
    if (editing) return;
    editing = true;
    const t = target();
    td.classList.add("relative");
    const snapshot = {
      targetMode: t.targetMode,
      targetDate: t.targetDate,
      targetLegacy: t.targetLegacy,
      target: t.target,
    };

    let committed = false;
    const panel = el("div", {
      class: "absolute left-0 top-full z-[40] mt-0.5 min-w-[10rem] rounded-md border border-gray-200 bg-white shadow-lg p-2 space-y-2",
      onclick: (e) => e.stopPropagation(),
      onmousedown: (e) => e.stopPropagation(),
    });

    const finish = (commit) => {
      if (!editing) return;
      editing = false;
      td.classList.remove("relative");
      const live = target();
      if (!commit) {
        live.targetMode = snapshot.targetMode;
        live.targetDate = snapshot.targetDate;
        live.targetLegacy = snapshot.targetLegacy;
        live.target = snapshot.target;
      } else {
        normalizeEngineTarget(live);
        stampRowChangedToday(live);
        onSave();
      }
      refreshStatic();
      td.replaceChildren(staticEl);
    };

    const dismiss = attachCellPopoverDismiss(td, () => {
      if (committed) return;
      finish(false);
    });

    const modeRow = el("div", { class: "flex flex-wrap gap-1" });
    const dateSlot = el("div", {});

    const paintDate = () => {
      dateSlot.replaceChildren();
      if (target().targetMode !== "date") return;
      const dateInp = el("input", {
        type: "date",
        class: "w-full rounded border border-dae/40 text-xs px-1 py-0.5",
        value: targetDateIso(),
        onclick: (e) => e.stopPropagation(),
        onmousedown: (e) => e.stopPropagation(),
      });
      dateInp.addEventListener("change", () => {
        const live = target();
        live.targetDate = cleanText(dateInp.value);
        live.targetLegacy = "";
        live.target = formatEngineTargetDisplay(live);
        committed = true;
        dismiss();
        finish(true);
      });
      dateSlot.appendChild(dateInp);
      setTimeout(() => dateInp.focus(), 0);
    };

    for (const o of ENGINE_TARGET_MODES) {
      const active = t.targetMode === o.value;
      modeRow.appendChild(el("button", {
        type: "button",
        class: `px-2 py-0.5 text-xs rounded border ${active ? "bg-dae text-white border-dae" : "border-gray-200 text-gray-800 hover:bg-gray-50"}`,
        text: o.label,
        onclick: (e) => {
          e.stopPropagation();
          const live = target();
          live.targetMode = o.value;
          if (o.value !== "date") {
            live.targetDate = "";
            live.targetLegacy = "";
            live.target = formatEngineTargetDisplay(live);
            committed = true;
            dismiss();
            finish(true);
            return;
          }
          live.target = formatEngineTargetDisplay(live);
          paintDate();
        },
      }));
    }

    panel.appendChild(modeRow);
    panel.appendChild(dateSlot);
    td.replaceChildren(panel);
    paintDate();
  };

  staticEl.addEventListener("mousedown", (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
  });
  staticEl.addEventListener("click", (ev) => {
    ev.stopPropagation();
    openEditor();
  });

  td.appendChild(staticEl);
  return td;
}

function openWidgetsDialog({ el, sections, widgets, onChange, onConfigured = null }) {
  if (!widgets || typeof widgets !== "object") return;
  const selected = new Map(sections.map((s) => [s.id, widgets[s.id] === true]));
  const backdrop = el("div", {
    class: "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4",
    "data-actracker-modal-backdrop": "1",
  });
  const modal = el("div", { class: "w-full max-w-lg rounded-xl bg-white shadow-lg border border-gray-200 p-5 max-h-[85vh] flex flex-col" });
  const list = el("div", { class: "space-y-2 my-4 overflow-y-auto flex-1" });
  const syncCheckbox = (id, on) => {
    const cb = list.querySelector(`input[data-widget-id="${id}"]`);
    if (cb) cb.checked = on;
  };
  const applySelection = () => {
    for (const [id, on] of selected) widgets[id] = on === true;
    onConfigured?.();
    onChange();
  };
  for (const sec of sections) {
    list.appendChild(
      el("label", {
        class: "flex items-center gap-2 text-sm cursor-pointer text-gray-800",
      }, [
        el("input", {
          type: "checkbox",
          "data-widget-id": sec.id,
          checked: selected.get(sec.id) === true,
          onchange: (ev) => {
            const checked = Boolean(ev.target?.checked);
            selected.set(sec.id, checked);
            if (checked && PROJECT_SECTION_WIDGET_IDS.includes(sec.id)) {
              selected.set("projects", true);
              syncCheckbox("projects", true);
            }
            applySelection();
          },
        }),
        el("span", { text: sec.label }),
      ])
    );
  }
  const close = () => {
    applySelection();
    backdrop.remove();
  };
  modal.appendChild(el("h3", { class: "text-lg font-semibold text-gray-900", text: "Visible widgets" }));
  modal.appendChild(el("p", { class: "text-sm text-gray-600", text: "Choose which sections appear for this operator. Your choices are saved for this operator. Uncheck any item to hide it." }));
  modal.appendChild(list);
  modal.appendChild(
    el("div", { class: "flex justify-end" }, [
      el("button", {
        type: "button",
        class: "px-4 py-2 text-sm text-white bg-dae rounded-lg hover:bg-dae/90",
        text: "Done",
        onclick: close,
      }),
    ])
  );
  backdrop.appendChild(modal);
  backdrop.addEventListener("click", (ev) => { if (ev.target === backdrop) close(); });
  document.body.appendChild(backdrop);
}

function openExportSectionsDialog(onConfirm, initialFlags = null) {
  const defaults = defaultEmailExportFlags();
  const selected = new Map(
    EXPORT_SECTIONS.map((s) => {
      if (initialFlags && typeof initialFlags === "object" && Object.prototype.hasOwnProperty.call(initialFlags, s.id)) {
        return [s.id, initialFlags[s.id] === true];
      }
      return [s.id, defaults[s.id] === true];
    }),
  );
  const backdrop = el("div", {
    class: "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4",
    "data-actracker-modal-backdrop": "1",
  });
  const modal = el("div", { class: "w-full max-w-md rounded-xl bg-white shadow-lg border border-gray-200 p-5" });
  const list = el("div", { class: "space-y-2 my-4 max-h-[50vh] overflow-y-auto" });
  for (const sec of EXPORT_SECTIONS) {
    list.appendChild(
      el("label", { class: "flex items-center gap-2 text-sm text-gray-800 cursor-pointer" }, [
        el("input", {
          type: "checkbox",
          checked: selected.get(sec.id),
          onchange: (ev) => selected.set(sec.id, Boolean(ev.target?.checked)),
        }),
        el("span", { text: sec.label }),
      ])
    );
  }
  const close = () => backdrop.remove();
  modal.appendChild(el("h3", { class: "text-lg font-semibold text-gray-900", text: "Export report sections" }));
  modal.appendChild(el("p", { class: "text-sm text-gray-600", text: "Defaults: A, B, C, F, L. Meeting minutes (L) is placed last in the email. Uncheck sections to omit them." }));
  modal.appendChild(list);
  modal.appendChild(
    el("div", { class: "flex justify-end gap-2" }, [
      el("button", {
        type: "button",
        class: "px-3 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50",
        text: "Cancel",
        onclick: close,
      }),
      el("button", {
        type: "button",
        class: "px-3 py-2 text-sm text-white bg-dae rounded-lg hover:bg-dae/90",
        text: "Export",
        onclick: () => {
          const flags = Object.fromEntries(EXPORT_SECTIONS.map((s) => [s.id, Boolean(selected.get(s.id))]));
          close();
          onConfirm(flags);
        },
      }),
    ])
  );
  backdrop.appendChild(modal);
  backdrop.addEventListener("click", (ev) => {
    if (ev.target === backdrop) close();
  });
  document.body.appendChild(backdrop);
}

function sortFleetRowsForProgress(rows) {
  const inSvc = [];
  const outSvc = [];
  for (const r of rows || []) {
    if (normaliseTam(r.tam) === "IN SERVICE") inSvc.push(r);
    else outSvc.push(r);
  }
  inSvc.sort((a, b) => {
    const aAir = a.status === "AIRBORNE" ? 0 : 1;
    const bAir = b.status === "AIRBORNE" ? 0 : 1;
    if (aAir !== bAir) return aAir - bAir;
    const aTog = Number.isFinite(a.togMinutes) ? a.togMinutes : 1e15;
    const bTog = Number.isFinite(b.togMinutes) ? b.togMinutes : 1e15;
    return aTog - bTog;
  });
  outSvc.sort((a, b) => {
    const aTog = Number.isFinite(a.togMinutes) ? a.togMinutes : -1;
    const bTog = Number.isFinite(b.togMinutes) ? b.togMinutes : -1;
    return bTog - aTog;
  });
  return [...inSvc, ...outSvc];
}

function sortEngineTracker(engines) {
  return [...(engines || [])]
    .map((e) => { normalizeEngineTarget(e); return e; })
    .sort((a, b) => {
    const au = String(a.azulStatus || "").toUpperCase() === "UNSERVICEABLE" ? 0 : 1;
    const bu = String(b.azulStatus || "").toUpperCase() === "UNSERVICEABLE" ? 0 : 1;
    if (au !== bu) return au - bu;
    const at = engineTargetSortKey(a);
    const bt = engineTargetSortKey(b);
    if (at !== bt) return at - bt;
    return String(a.esn || "").localeCompare(String(b.esn || ""));
  });
}

function tamByMsn(fleetRows) {
  const map = new Map();
  for (const r of fleetRows || []) {
    map.set(cleanText(r.msn), normaliseTam(r.tam));
  }
  return map;
}

function operatorDirectoryNames(operators) {
  return Array.from(new Set(
    (Array.isArray(operators) ? operators : [])
      .map((o) => cleanText(typeof o === "string" ? o : o?.name))
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));
}

function syncOperatorPickerOptions(selectEl, opNames, selectedValue, loading) {
  if (!selectEl) return;
  const want = cleanText(selectedValue);
  selectEl.disabled = Boolean(loading);
  const names = Array.isArray(opNames) ? opNames : [];
  if (
    selectEl.options.length === names.length + 1
    && selectEl.options[0]?.value === ""
    && names.every((n, i) => selectEl.options[i + 1]?.value === n)
  ) {
    selectEl.value = want && names.includes(want) ? want : "";
    return;
  }
  selectEl.replaceChildren();
  const blank = document.createElement("option");
  blank.value = "";
  blank.textContent = loading ? "Loading operators…" : "Select operator…";
  selectEl.appendChild(blank);
  for (const n of names) {
    const opt = document.createElement("option");
    opt.value = n;
    opt.textContent = n;
    selectEl.appendChild(opt);
  }
  selectEl.value = want && names.includes(want) ? want : "";
}

function buildOperatorPicker({ opNames, selectedValue, loading, loadError, onPick, onRetry }) {
  if (!opProgressPicker) {
    const wrap = document.createElement("div");
    wrap.className = "min-w-[200px] max-w-md flex-1";
    const label = document.createElement("label");
    label.className = "block text-xs font-medium text-gray-600 mb-1";
    label.htmlFor = "op-progress-operator";
    label.textContent = "Operator";
    const selectEl = document.createElement("select");
    selectEl.id = "op-progress-operator";
    selectEl.className = "rounded-lg border border-gray-200 px-3 py-2 text-sm w-full max-w-md text-gray-800";
    selectEl.addEventListener("change", (ev) => {
      ev.target.blur();
      const fn = opProgressPicker?.onPick;
      if (typeof fn === "function") fn(cleanText(ev.target.value));
    });
    const meta = document.createElement("p");
    meta.className = "text-[11px] text-gray-400 mt-1";
    const err = document.createElement("p");
    err.className = "text-xs text-red-600 mt-1 hidden";
    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "mt-1 text-xs text-dae font-medium hover:underline hidden";
    retry.textContent = "Retry load operators";
    wrap.append(label, selectEl, meta, err, retry);
    opProgressPicker = { wrap, selectEl, meta, err, retry, onPick: null };
  }
  opProgressPicker.onPick = onPick;
  syncOperatorPickerOptions(opProgressPicker.selectEl, opNames, selectedValue, loading);
  if (!loading && opNames.length) {
    opProgressPicker.meta.className = "text-[11px] text-gray-400 mt-1";
    opProgressPicker.meta.textContent = `${opNames.length} operators in directory`;
    opProgressPicker.meta.classList.remove("hidden");
  } else {
    opProgressPicker.meta.textContent = "";
    opProgressPicker.meta.classList.add("hidden");
  }
  if (loadError) {
    opProgressPicker.err.textContent = `Could not load operators: ${loadError}`;
    opProgressPicker.err.classList.remove("hidden");
  } else {
    opProgressPicker.err.classList.add("hidden");
  }
  if (loadError || (!loading && !opNames.length)) {
    opProgressPicker.retry.classList.remove("hidden");
    opProgressPicker.retry.onclick = onRetry;
  } else {
    opProgressPicker.retry.classList.add("hidden");
  }
  return opProgressPicker.wrap;
}

function collectFleetRows(ctx, operator, modelFamilies) {
  const { state, aircraftModelFamily, aircraftRowForTable } = ctx;
  const families = new Set((modelFamilies || []).map((f) => cleanText(f)).filter(Boolean));
  const list = (state.aircraft || []).filter((a) => {
    if (!aircraftMatchesOperator(a, operator, state.operators)) return false;
    if (families.size && !families.has(aircraftModelFamily(a))) return false;
    return true;
  });
  return sortFleetRowsForProgress(
    list.map((a) => ({
      ...aircraftRowForTable(a),
      family: aircraftModelFamily(a),
    }))
  );
}

function buildTamSummary(rows) {
  const map = new Map();
  for (const r of rows) {
    const tam = normaliseTam(r.tam);
    map.set(tam, (map.get(tam) || 0) + 1);
  }
  return Array.from(map.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([tam, count]) => ({ tam, count }));
}

function buildGroundHistogram(rows, modelFamilies = []) {
  const families = (modelFamilies || []).map((f) => cleanText(f)).filter(Boolean);
  const multiFamily = families.length > 1;
  const total = rows.length;

  const makeRow = (label, matched) => ({
    label,
    count: matched.length,
    pct: pct(matched.length, total),
    familyCounts: multiFamily ? countRowsByFamily(matched, families) : null,
  });

  const inService = rows.filter((r) => normaliseTam(r.tam) === "IN SERVICE");
  const notInService = rows.filter((r) => normaliseTam(r.tam) !== "IN SERVICE");
  const out = [makeRow("In service", inService)];
  for (const b of GROUND_BUCKETS) {
    if (b.key === "unknown") {
      const matched = notInService.filter((r) => !Number.isFinite(r.togMinutes));
      if (matched.length) out.push(makeRow(b.label, matched));
      continue;
    }
    const matched = notInService.filter((r) => {
      const m = r.togMinutes;
      if (!Number.isFinite(m)) return false;
      return m >= b.min && m < b.max;
    });
    if (matched.length) out.push(makeRow(b.label, matched));
  }

  const totalFamilyCounts = multiFamily ? countRowsByFamily(rows, families) : null;
  const totalRow = {
    label: "Total",
    count: total,
    pct: "100.0%",
    familyCounts: totalFamilyCounts,
    isTotal: true,
  };

  return {
    rows: out,
    totalRow,
    families: multiFamily ? families : [],
    multiFamily,
    total,
    longTermGrounded: notInService.filter((r) => Number.isFinite(r.togMinutes) && r.togMinutes >= 365 * 24 * 60).length,
  };
}

function buildEngineSummary(engineTracker) {
  let serviceable = 0;
  let unserviceable = 0;
  let inShop = 0;
  let inStorage = 0;
  for (const e of engineTracker || []) {
    if (String(e.azulStatus || "").toUpperCase() === "SERVICEABLE") serviceable += 1;
    else {
      unserviceable += 1;
      const disp = String(e.disposition || "").toLowerCase();
      if (disp === "in_shop") inShop += 1;
      else if (disp === "in_storage") inStorage += 1;
    }
  }
  return { serviceable, unserviceable, inShop, inStorage, total: serviceable + unserviceable };
}

function partsByMsn(mroPipeline) {
  const map = new Map();
  for (const row of mroPipeline || []) map.set(cleanText(row.msn), String(row.parts || ""));
  return map;
}

function buildReportContext(ctx, { operator, modelFamilies, store }) {
  const fleetRows = collectFleetRows(ctx, operator, modelFamilies);
  const partsMap = partsByMsn(store.mroPipeline);
  const fleetWithParts = fleetRows.map((r) => ({
    ...r,
    parts: partsMap.get(cleanText(r.msn)) || "",
  }));
  const now = new Date();
  return {
    operator,
    modelFamilies,
    fleetRows: fleetWithParts,
    total: fleetRows.length,
    tamSummary: buildTamSummary(fleetRows),
    ground: buildGroundHistogram(fleetRows, modelFamilies),
    engineSummary: buildEngineSummary(store.engineTracker),
    store,
    tamByMsn: tamByMsn(fleetRows),
    weekMeta: reportWeekMeta(now),
    generatedAtEst: formatMaterialsExportTimeEst(now),
    headerLine: formatReportHeaderLine(reportWeekMeta(now), now, fleetRows.length, modelFamilies),
    subject: reportSubject(operator, now),
    sortedEngines: sortEngineTracker(store.engineTracker),
  };
}

/** Chrome-safe dismiss for in-cell popovers (native select menus render outside the cell). */
function attachCellPopoverDismiss(td, onDismiss) {
  let closed = false;
  const cleanup = () => {
    document.removeEventListener("mousedown", onDoc, true);
    document.removeEventListener("keydown", onKey, true);
  };
  const shutdown = () => {
    if (closed) return;
    closed = true;
    cleanup();
    delete td.dataset.opEditing;
    onDismiss();
  };
  const onKey = (ev) => {
    if (ev.key === "Escape") {
      ev.preventDefault();
      shutdown();
    }
  };
  const onDoc = (ev) => {
    if (td.contains(ev.target)) return;
    shutdown();
  };
  td.dataset.opEditing = "1";
  setTimeout(() => {
    document.addEventListener("mousedown", onDoc, true);
    document.addEventListener("keydown", onKey, true);
  }, 200);
  return shutdown;
}

function openCellOptionMenu(td, options, current, onPick) {
  let committed = false;
  const menu = el("div", {
    class: "fixed z-[200] min-w-[6.5rem] max-w-[16rem] rounded-md border border-gray-200 bg-white shadow-lg py-1 max-h-[min(280px,50vh)] overflow-y-auto",
    role: "listbox",
    "data-op-cell-menu": "1",
    onclick: (e) => e.stopPropagation(),
    onmousedown: (e) => e.stopPropagation(),
  });
  const positionMenu = () => {
    const rect = td.getBoundingClientRect();
    menu.style.left = `${Math.max(8, rect.left)}px`;
    menu.style.top = `${rect.bottom + 2}px`;
    menu.style.minWidth = `${Math.max(rect.width, 104)}px`;
  };
  const finish = (value) => {
    cleanup();
    delete td.dataset.opEditing;
    onPick(value);
  };
  let closed = false;
  const cleanup = () => {
    if (closed) return;
    closed = true;
    document.removeEventListener("mousedown", onDoc, true);
    document.removeEventListener("keydown", onKey, true);
    window.removeEventListener("scroll", positionMenu, true);
    window.removeEventListener("resize", positionMenu);
    if (menu.isConnected) menu.remove();
  };
  const onKey = (ev) => {
    if (ev.key === "Escape") {
      ev.preventDefault();
      if (!committed) finish(null);
      else cleanup();
    }
  };
  const onDoc = (ev) => {
    if (td.contains(ev.target) || menu.contains(ev.target)) return;
    if (!committed) finish(null);
    else cleanup();
  };
  td.dataset.opEditing = "1";
  const cur = String(current ?? "");
  for (const o of options) {
    const picked = String(o.value) === cur;
    menu.appendChild(el("button", {
      type: "button",
      role: "option",
      class: `block w-full text-left px-3 py-1.5 text-xs hover:bg-red-50 ${picked ? "bg-red-50 font-semibold text-dae" : "text-gray-800"}`,
      text: o.label,
      onmousedown: (e) => {
        e.preventDefault();
        e.stopPropagation();
        committed = true;
        finish(String(o.value));
      },
    }));
  }
  document.body.appendChild(menu);
  positionMenu();
  setTimeout(() => {
    document.addEventListener("mousedown", onDoc, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", positionMenu, true);
    window.addEventListener("resize", positionMenu);
  }, 0);
}

/** Click-to-edit date cell: display dd-mmm-yy (or TBD), edit with native date picker + Clear. */
function clickEditDateCell({ value, onCommit, className = "px-2 py-1", emptyLabel = "TBD" }) {
  let current = normalizeMtxDate(value);
  const td = el("td", { class: `${className} align-top whitespace-nowrap` });
  const staticEl = el("span", {
    class: "block min-h-[1.25rem] cursor-text rounded px-1 -mx-1 hover:bg-red-50 text-gray-800",
  });

  const refreshDisplay = () => {
    if (current) {
      staticEl.textContent = fmtDateDdMmmYy(current);
      staticEl.classList.remove("text-gray-400", "italic");
      staticEl.classList.add("text-gray-800");
    } else {
      staticEl.textContent = emptyLabel || "TBD";
      staticEl.classList.add("text-gray-400", "italic");
      staticEl.classList.remove("text-gray-800");
    }
  };
  refreshDisplay();

  staticEl.addEventListener("mousedown", (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
  });
  staticEl.addEventListener("click", (ev) => {
    ev.stopPropagation();
    const wrap = el("div", {
      class: "flex flex-col gap-1 min-w-[9rem]",
      onclick: (e) => e.stopPropagation(),
      onmousedown: (e) => e.stopPropagation(),
    });
    const dateInp = el("input", {
      type: "date",
      class: "w-full min-w-[8.5rem] rounded border border-dae/40 px-1 py-0.5 text-xs",
      value: mtxDateToIso(current),
      onclick: (e) => e.stopPropagation(),
      onmousedown: (e) => e.stopPropagation(),
    });
    const clearBtn = el("button", {
      type: "button",
      class: "text-[10px] text-dae hover:underline self-start px-1",
      text: "Clear → TBD",
      onclick: (e) => e.stopPropagation(),
      onmousedown: (e) => e.stopPropagation(),
    });
    td.dataset.opEditing = "1";
    let done = false;
    const finish = (commit, nextValue = undefined) => {
      if (done) return;
      done = true;
      dismiss();
      delete td.dataset.opEditing;
      if (commit) {
        // Explicit clear (null/empty) or a picked date — never ignore cleared dates.
        if (nextValue === null || nextValue === "") {
          current = "";
          onCommit("");
        } else if (typeof nextValue === "string") {
          current = normalizeMtxDate(nextValue);
          onCommit(current);
        } else if (dateInp.value) {
          current = mtxDateFromIso(dateInp.value);
          onCommit(current);
        } else {
          current = "";
          onCommit("");
        }
      }
      refreshDisplay();
      td.replaceChildren(staticEl);
    };
    const dismiss = attachCellPopoverDismiss(td, () => finish(false));
    dateInp.addEventListener("change", () => {
      // Empty value from native clear / user wipe → save as TBD.
      finish(true, dateInp.value || "");
    });
    clearBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      dateInp.value = "";
      finish(true, "");
    });
    wrap.appendChild(dateInp);
    wrap.appendChild(clearBtn);
    td.replaceChildren(wrap);
    setTimeout(() => dateInp.focus(), 0);
  });

  td.appendChild(staticEl);
  return td;
}

/** Click-to-edit cell: static until clicked; saves on blur (text) or pick (options). */
function clickEditCell({
  value,
  onCommit,
  multiline = false,
  selectOptions = null,
  readOnly = false,
  className = "px-2 py-1",
  renderDisplay = null,
}) {
  let current = value;
  let editing = false;
  const td = el("td", { class: `${className} align-top` });
  const staticEl = el("span", {
    class: readOnly
      ? "block min-h-[1.25rem] whitespace-pre-wrap text-gray-700"
      : selectOptions
        ? "block min-h-[1.25rem] cursor-pointer rounded px-1 -mx-1 hover:bg-red-50"
        : "block min-h-[1.25rem] whitespace-pre-wrap cursor-text rounded px-1 -mx-1 hover:bg-red-50",
  });

  const refreshDisplay = () => {
    if (renderDisplay) {
      staticEl.replaceChildren();
      const node = renderDisplay(current);
      if (node) staticEl.appendChild(node);
      else staticEl.textContent = "—";
    } else {
      staticEl.textContent = current == null || current === "" ? "—" : String(current);
    }
  };
  refreshDisplay();

  const closeEditor = (inputEl, { commit }) => {
    if (commit) {
      const next = inputEl.value;
      onCommit(next);
      current = next;
    }
    refreshDisplay();
    td.replaceChildren(staticEl);
    editing = false;
  };

  const attachTextEditor = (inputEl) => {
    td.dataset.opEditing = "1";
    let closed = false;
    const shutdown = (commit) => {
      if (closed) return;
      closed = true;
      cleanup();
      closeEditor(inputEl, { commit });
    };
    const onBlur = () => shutdown(true);
    const cleanup = () => {
      inputEl.removeEventListener("blur", onBlur);
      inputEl.removeEventListener("keydown", onKey);
      delete td.dataset.opEditing;
    };
    const onKey = (ev) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        shutdown(false);
      } else if (!multiline && ev.key === "Enter") {
        ev.preventDefault();
        inputEl.blur();
      }
    };
    inputEl.addEventListener("blur", onBlur);
    inputEl.addEventListener("keydown", onKey);
  };

  if (!readOnly) {
    staticEl.addEventListener("mousedown", (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
    });
    staticEl.addEventListener("click", (ev) => {
      ev.stopPropagation();
      if (editing) return;
      editing = true;

      if (selectOptions) {
        openCellOptionMenu(td, selectOptions, current, (picked) => {
          editing = false;
          if (picked != null) {
            onCommit(picked);
            current = picked;
          }
          refreshDisplay();
          td.replaceChildren(staticEl);
        });
        return;
      }
      if (multiline) {
        const ta = el("textarea", {
          rows: 3,
          class: "w-full min-w-[160px] rounded border border-dae/40 px-2 py-1 text-xs",
          value: String(current ?? ""),
          onclick: (e) => e.stopPropagation(),
          onmousedown: (e) => e.stopPropagation(),
        });
        attachTextEditor(ta);
        td.replaceChildren(ta);
        ta.focus();
        return;
      }
      const inp = el("input", {
        type: "text",
        class: "w-full min-w-[72px] rounded border border-dae/40 px-2 py-1 text-xs",
        value: String(current ?? ""),
        onclick: (e) => e.stopPropagation(),
        onmousedown: (e) => e.stopPropagation(),
      });
      attachTextEditor(inp);
      td.replaceChildren(inp);
      inp.focus();
      inp.select();
    });
  }
  td.appendChild(staticEl);
  return td;
}

function outlookCell(text, { header = false, bg = "#ffffff", color = "#1e1f24", bold = false, width = null } = {}) {
  const style = [
    `border:1px solid #d8dbe0`,
    `padding:6px 8px`,
    `font-family:Calibri,Arial,sans-serif`,
    `font-size:12px`,
    `vertical-align:top`,
    `background:${header ? DAE_RED : bg}`,
    `color:${header ? "#ffffff" : color}`,
    bold ? "font-weight:bold" : "",
    width ? `width:${width}` : "",
  ]
    .filter(Boolean)
    .join(";");
  const tag = header ? "th" : "td";
  return `<${tag} style="${style}">${text}</${tag}>`;
}

function outlookTable(headers, rows) {
  const head = `<tr>${headers.map((h) => {
    const text = typeof h === "object" && h != null ? h.text : h;
    const width = typeof h === "object" && h != null ? h.width : null;
    const inner = String(text).includes("\n")
      ? String(text).split("\n").map((line) => htmlEscape(line)).join("<br>")
      : htmlEscape(text);
    return outlookCell(inner, {
      header: true,
      width: width || (String(text).includes("\n") ? "52px" : null),
    });
  }).join("")}</tr>`;
  const body = rows.map((cells) => `<tr>${cells.map((c) => (typeof c === "string" && c.startsWith("<td") ? c : outlookCell(c))).join("")}</tr>`).join("");
  return `<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;margin:12px 0;">${head}${body}</table>`;
}

function outlookCard(label, value, sub) {
  return `<td style="border:1px solid #e5e7eb;border-top:3px solid ${DAE_RED};padding:12px 14px;background:#fff;vertical-align:top;">
    <div style="font-size:24px;font-weight:bold;color:#111;">${htmlEscape(value)}</div>
    <div style="font-size:10px;color:#6b7280;text-transform:uppercase;margin-top:4px;">${htmlEscape(label)}${sub ? ` · ${htmlEscape(sub)}` : ""}</div>
  </td>`;
}

function outlookEngineTotalCard(count) {
  return `<td style="border:1px solid #e5e7eb;border-top:3px solid ${DAE_RED};padding:12px 14px;background:#fff;vertical-align:top;">
    <div style="font-size:24px;font-weight:bold;color:#111;">${htmlEscape(count)}</div>
    <div style="font-size:10px;color:#6b7280;text-transform:uppercase;margin-top:6px;line-height:1.35;">${htmlEscape(ENGINES_UNSERVICEABLE_FILING_NOTE)}</div>
    <div style="font-size:10px;color:#6b7280;text-transform:uppercase;margin-top:4px;">Total engines · 100.0%</div>
  </td>`;
}

function outlookEngineUnserviceableCard(count) {
  return `<td style="border:1px solid #e5e7eb;border-top:3px solid ${DAE_RED};padding:12px 14px;background:#fff;vertical-align:top;">
    <div style="font-size:24px;font-weight:bold;color:#111;">${htmlEscape(count)}</div>
    <div style="font-size:10px;color:#6b7280;text-transform:uppercase;margin-top:4px;">Still unserviceable</div>
  </td>`;
}

function groundHistogramHeaders(ground) {
  const base = ["Period", "A/C"];
  if (ground.multiFamily) base.push(...ground.families);
  return base;
}

function groundHistogramOutlookRows(ground) {
  const allRows = [...(ground.rows || []), ground.totalRow].filter(Boolean);
  return allRows.map((r) => {
    const isTotal = Boolean(r.isTotal);
    const cells = [r.label, String(r.count)];
    if (ground.multiFamily) {
      for (const fam of ground.families) cells.push(String(r.familyCounts?.[fam] ?? 0));
    }
    return cells.map((c, i) => {
      const bold = isTotal && i < 2 ? "font-weight:bold;background:#f9fafb;" : isTotal ? "background:#f9fafb;" : "";
      return `<td style="border:1px solid #d8dbe0;padding:6px 8px;font-family:Calibri,Arial,sans-serif;font-size:12px;vertical-align:top;${bold}">${typeof c === "string" && c.startsWith("<") ? c : htmlEscape(String(c ?? ""))}</td>`;
    });
  });
}

function outlookGroundTable(headers, rows) {
  const head = `<tr>${headers.map((h) => outlookCell(htmlEscape(h), { header: true })).join("")}</tr>`;
  const body = rows.map((cells) => `<tr>${cells.join("")}</tr>`).join("");
  return `<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:auto;max-width:520px;margin:12px 0;">${head}${body}</table>`;
}

function buildOutlookServiceabilityHtml(store) {
  const rows = store.serviceability || [];
  const thStyle = `border:1px solid #d8dbe0;padding:6px 8px;font-family:Calibri,Arial,sans-serif;font-size:11px;background:${DAE_RED};color:#fff;font-weight:bold;text-align:center;`;
  const tdStyle = `border:1px solid #d8dbe0;padding:4px 6px;font-family:Calibri,Arial,sans-serif;font-size:11px;vertical-align:top;text-align:center;`;
  const tdLeft = `${tdStyle}text-align:left;`;
  const th = (text, attrs = "") => `<th style="${thStyle}" ${attrs}>${htmlEscape(text)}</th>`;
  const td = (text, left = false, bg = "#ffffff") =>
    `<td style="${left ? tdLeft : tdStyle}background:${bg};">${htmlEscape(String(text ?? ""))}</td>`;

  const head1 = [
    th("MSN", 'rowspan="2"'),
    th("Airframe", `colspan="${AF_KEYS.length}"`),
    th("Landing gear", `colspan="${LG_KEYS.length}"`),
    th("Propellers", `colspan="${PROP_KEYS.length}"`),
    th("Eng 1", 'colspan="2"'),
    th("Eng 2", 'colspan="2"'),
    th("Comments", 'rowspan="2"'),
  ].join("");
  const head2 = [
    ...AF_KEYS.map((k) => th(k)),
    ...LG_KEYS.map((k) => th(k)),
    ...PROP_KEYS.map((k) => th(k)),
    th("ESN"), th("S/U"), th("ESN"), th("S/U"),
  ].join("");

  const body = rows
    .map((row, idx) => {
      const bg = idx % 2 === 0 ? "#ffffff" : "#f9fafb";
      const su = (v) => {
        const u = String(v || "S").toUpperCase() === "U";
        const weight = u ? "font-weight:bold;" : "font-weight:normal;";
        return `<td style="${tdStyle}background:${bg};color:#1e1f24;${weight}">${htmlEscape(u ? "U" : "S")}</td>`;
      };
      return `<tr>
${td(row.msn, true, bg)}
${AF_KEYS.map((k) => su(row.airframe?.[k] || "S")).join("")}
${LG_KEYS.map((k) => su(row.landingGear?.[k] || "S")).join("")}
${PROP_KEYS.map((k) => su(row.propellers?.[k] || "S")).join("")}
${td(row.eng1?.esn || "", false, bg)}${su(row.eng1?.status || "S")}
${td(row.eng2?.esn || "", false, bg)}${su(row.eng2?.status || "S")}
<td style="${tdLeft}background:${bg};">${htmlEscape(String(row.comments || "")).replace(/\n/g, "<br>")}</td>
</tr>`;
    })
    .join("");

  return `<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;margin:12px 0;">
<thead><tr>${head1}</tr><tr>${head2}</tr></thead>
<tbody>${body || `<tr><td colspan="20" style="${tdLeft}">No serviceability data</td></tr>`}</tbody>
</table>`;
}

function renderGroundHistogramTable(ground, sortState, onSort) {
  const headers = groundHistogramHeaders(ground);
  const headerCells = headers.map((h, i) => {
    const keys = ["label", "count", ...(ground.multiFamily ? ground.families : [])];
    const key = keys[i];
    return key ? sortableTh(h, key, sortState, onSort) : el("th", { class: "px-2 py-2 font-medium", text: h });
  });
  let dataRows = [...(ground.rows || [])];
  if (sortState?.key) {
    dataRows = applyTableSort(dataRows, sortState, (r) => groundRowSortValue(r, sortState.key, ground));
  }
  const allRows = [...dataRows, ground.totalRow];
  const body = allRows.map((r) => {
    const isTotal = Boolean(r.isTotal);
    const cells = [
      el("td", { class: `px-2 py-1 ${isTotal ? "font-semibold" : ""}`, text: r.label }),
      el("td", { class: `px-2 py-1 text-right font-mono ${isTotal ? "font-semibold" : ""}`, text: String(r.count) }),
    ];
    if (ground.multiFamily) {
      for (const fam of ground.families) {
        cells.push(el("td", { class: "px-2 py-1 text-right font-mono", text: String(r.familyCounts?.[fam] ?? 0) }));
      }
    }
    return el("tr", { class: isTotal ? "border-t-2 border-gray-300 bg-gray-50 font-medium" : "border-t border-gray-100" }, cells);
  });
  return el("div", { class: "inline-block max-w-lg overflow-x-auto border border-gray-200 rounded-xl" }, [
    el("table", { class: "w-auto text-left text-xs" }, [
      el("thead", { class: "bg-dae text-white uppercase" }, [el("tr", {}, headerCells)]),
      el("tbody", {}, body),
    ]),
  ]);
}

function buildOutlookHtml(reportCtx, sections = null) {
  const flags = sections || defaultEmailExportFlags();
  const include = (id) => flags[id] === true;
  const {
    operator, total, tamSummary, engineSummary, store, weekMeta, headerLine, subject, sortedEngines, modelFamilies,
    sortedMro, sortedServiceability, sortedFleetRows, groundSorted,
  } = reportCtx;
  const ground = groundSorted || reportCtx.ground;
  const fleetRows = sortedFleetRows || reportCtx.fleetRows;
  const modelScope = formatModelScopeLabel(modelFamilies);
  const metaLine = headerLine || formatReportHeaderLine(weekMeta, new Date(), total, modelFamilies);

  const parts = [
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${htmlEscape(subject)}</title></head>`,
    `<body style="margin:0;padding:16px;background:#f5f6f7;font-family:Calibri,Arial,sans-serif;color:#1e1f24;">`,
    `<table role="presentation" cellpadding="0" cellspacing="0" style="width:100%;max-width:1125px;margin:0 auto;background:#f5f6f7;"><tr><td>`,
    `<div style="font-size:20px;font-weight:bold;color:${DAE_RED};text-transform:uppercase;">${htmlEscape(operator)} — Progress report</div>`,
    `<div style="font-size:12px;color:#5b6168;margin:4px 0 16px;">${htmlEscape(metaLine)}</div>`,
  ];

  if (include("fleetSummary")) {
    const tamCards = tamSummary.map(({ tam, count }) => outlookCard(tam, count, pct(count, total))).join("");
    parts.push(
      outlookSectionTitle(exportSectionLabel("fleetSummary")),
      `<div style="font-size:11px;color:#6b7280;margin-bottom:8px;">Models in scope: ${htmlEscape(modelScope)}</div>`,
      `<table role="presentation" cellpadding="0" cellspacing="8" style="width:100%;border-collapse:separate;"><tr>`,
      `${outlookCard("Aircraft in portfolio", total, "")}${tamCards}`,
      `</tr></table>`
    );
  }

  if (include("enginesSummary")) {
    const engTotal = engineSummary.total || 1;
    const engineCards = [
      outlookEngineTotalCard(engineSummary.total),
      outlookCard("Now serviceable", engineSummary.serviceable, pct(engineSummary.serviceable, engTotal)),
      outlookEngineUnserviceableCard(engineSummary.unserviceable),
    ].join("");
    parts.push(
      outlookSectionTitle(exportSectionLabel("enginesSummary")),
      `<table role="presentation" cellpadding="0" cellspacing="8" style="width:100%;border-collapse:separate;"><tr>${engineCards}</tr></table>`
    );
  }

  if (include("ground")) {
    const histRows = groundHistogramOutlookRows(ground);
    parts.push(
      outlookSectionTitle(exportSectionLabel("ground")),
      outlookGroundTable(groundHistogramHeaders(ground), histRows)
    );
  }

  if (include("rts")) {
    const rtsSource = sortedMro?.length
      ? sortedMro
      : sortRtsRows(
          (store.mroPipeline || []).map((r, i) => ({ ...r, _idx: i + 1 })),
          { key: "mtxStart", dir: "asc" },
        );
    const rtsOrdered = sortRtsRows(rtsSource, { key: "mtxStart", dir: "asc" });
    const rtsRows = rtsOrdered.map((r, i) => {
      const tat = computeTatDays(r.mtxStart, r.mtxComplete) || r.tatDays || "";
      const td = (v) => reportTd(htmlEscape(String(v ?? "")));
      const tdStatus = reportTd(outlookRtsPill(r.rtsStatus));
      const tdParts = reportTd(chronologyHtmlForPrint(rtsPartsTracker(r).history, htmlEscape));
      return [
        td(i + 1), td(r.msn), td(r.reg), td(r.mtxRequirement), tdStatus,
        td(fmtDateDdMmmYy(r.mtxStart, { empty: "TBD" })), td(fmtDateDdMmmYy(r.mtxComplete, { empty: "TBD" })),
        td(tat), td(r.mro), td(r.serviceabilityTable), tdParts,
      ];
    });
    parts.push(
      outlookSectionTitle(exportSectionLabel("rts")),
      outlookTable(
        ["#", "MSN", "Reg", "MTX requirement", "Status", "MTX start", "MTX complete", "TAT [DY]", "MRO", RTS_FILING_SVC_HEADER, "Parts / comments"],
        rtsRows
      )
    );
  }

  if (include("serviceability")) {
    parts.push(
      outlookSectionTitle(exportSectionLabel("serviceability")),
      buildOutlookServiceabilityHtml({ serviceability: sortedServiceability || store.serviceability })
    );
  }

  if (include("engineTracker")) {
    const engRows = (sortedEngines || []).map((e) => {
      normalizeEngineTarget(e);
      const notes = engineReasonTracker(e).history;
      const td = (v) => reportTd(htmlEscape(String(v ?? "")));
      const tdStatus = reportTd(outlookEngineStatusPill(e.azulStatus));
      const dispPill = outlookDispositionPill(e.disposition);
      const tdDisp = reportTd(dispPill || "—");
      const tdReason = reportTd(chronologyHtmlForPrint(notes, htmlEscape));
      return [
        td(e.esn), tdStatus, tdDisp, td(e.location), td(formatEngineTargetDisplay(e)),
        td(e.fromServiceability === "true" || e.fromServiceability === true ? "●" : ""), tdReason,
      ];
    });
    parts.push(
      outlookSectionTitle(exportSectionLabel("engineTracker")),
      outlookTable(["ESN", "Status", "Disposition", "Location", "Target", "Svc", "Reason / notes"], engRows)
    );
  }

  if (include("fleetSnapshot")) {
    const fleetTableRows = fleetRows.map((r) => {
      const tdStyle = `border:1px solid #d8dbe0;padding:6px 8px;font-family:Calibri,Arial,sans-serif;font-size:12px;vertical-align:top;background:#fff;`;
      const td = (v) => `<td style="${tdStyle}">${htmlEscape(String(v ?? ""))}</td>`;
      const tdStatus = `<td style="${tdStyle}">${outlookTamPill(r.tam)}</td>`;
      const tdParts = `<td style="${tdStyle}">${htmlEscape(String(r.parts || "")).replace(/\n/g, "<br>")}</td>`;
      return [
        td(r.msn), td(r.reg), td(r.model), tdStatus,
        td(r.tog || (r.status === "AIRBORNE" ? "AIRBORNE" : "—")), td(r.loc), tdParts,
      ];
    });
    parts.push(
      outlookSectionTitle(exportSectionLabel("fleetSnapshot")),
      outlookTable(["MSN", "Reg", "Model", "Status", "Time on ground", "Location", "Parts / comments"], fleetTableRows)
    );
  }

  if (include("heatmap")) {
    const legend = Array.from(new Set(fleetRows.map((r) => normaliseTam(r.tam))))
      .map((tam) => `<span style="display:inline-block;margin-right:12px;font-size:11px;"><span style="display:inline-block;width:12px;height:12px;background:${TAM_HEX[tam] || "#374151"};border-radius:2px;margin-right:4px;"></span>${htmlEscape(tam)}</span>`)
      .join("");
    const heatRows = [];
    for (let i = 0; i < fleetRows.length; i += 4) {
      heatRows.push(
        fleetRows.slice(i, i + 4).map((r) => {
          const tam = normaliseTam(r.tam);
          const color = TAM_HEX[tam] || "#374151";
          const modelEngine = [r.model, r.engine].filter((x) => x && x !== "—").join(" · ");
          return `<td style="padding:4px;width:25%;vertical-align:top;"><div style="background:${color};color:#fff;border-radius:8px;padding:8px;font-size:11px;"><strong>MSN ${htmlEscape(r.msn)}</strong><br>${htmlEscape(r.reg)}<br>${htmlEscape(modelEngine || "—")}<br>${htmlEscape(tam)}<br>${htmlEscape(r.tog || (r.status === "AIRBORNE" ? "AIRBORNE" : "—"))}</div></td>`;
        }).join("")
      );
    }
    parts.push(
      outlookSectionTitle(exportSectionLabel("heatmap")),
      `<div style="margin-bottom:10px;">${legend}</div>`,
      heatRows.map((row) => `<table role="presentation" cellpadding="0" cellspacing="0" style="width:100%;margin-bottom:4px;"><tr>${row}</tr></table>`).join("")
    );
  }

  const openSurv = (reportCtx.surveillanceCampaigns || reportCtx.openSurveillances || [])
    .map((c) => (c?.summary ? c : { summary: c, aircraft: [] }))
    .filter((c) => !["completed", "cancelled"].includes(String(c.summary?.status || "").toLowerCase()));
  if (include("surveillances") && openSurv.length) {
    parts.push(outlookSectionTitle(exportSectionLabel("surveillances")));
    for (const camp of openSurv) {
      const s = camp.summary || {};
      const aircraft = camp.aircraft || [];
      parts.push(`<p><strong>${htmlEscape(s.quarter)}-${htmlEscape(String(s.year))}</strong> — ${htmlEscape(String(aircraft.length || s.aircraftCount || 0))} aircraft</p>`);
      if (aircraft.length) {
        parts.push("<table border='1' cellpadding='4' cellspacing='0' style='border-collapse:collapse;font-size:12px;margin-bottom:12px;'><tr><th>MSN</th><th>Reg</th><th>Records</th><th>Physical</th><th>Overall</th><th>Insp. date</th></tr>");
        for (const a of aircraft) {
          parts.push(`<tr><td>${htmlEscape(a.msn)}</td><td>${htmlEscape(a.registration)}</td><td>${htmlEscape(String(a.recordsPct ?? 0))}%</td><td>${htmlEscape(String(a.physicalPct ?? 0))}%</td><td>${htmlEscape(a.overallStatus)}</td><td>${htmlEscape(a.actualInspectionDate || a.expectedInspectionDate || "")}</td></tr>`);
        }
        parts.push("</table>");
      }
    }
  }

  const exportProject = reportCtx.activeProject || activeProject(store, store?.activeProjectId);
  // Project sections except meeting minutes (L is always last in the email).
  if (exportProject && (include("projects") || anyProjectSectionOn({ ...flags, minutes: false }))) {
    parts.push(buildProjectsOutlookHtml(exportProject, { ...flags, minutes: false }, htmlEscape, outlookSectionTitle));
  }

  if (include("minutes") && exportProject) {
    parts.push(buildMinutesOutlookHtml(exportProject, htmlEscape, outlookSectionTitle));
  }

  parts.push(
    `<div style="margin-top:24px;padding-top:10px;border-top:2px solid ${DAE_RED};font-size:11px;color:#6b7280;">End of report — ${htmlEscape(metaLine)}</div>`,
    `</td></tr></table></body></html>`
  );
  return parts.join("\n");
}

function buildReportHtml(reportCtx, sections) {
  return buildOutlookHtml(reportCtx, sections);
}

function buildEmlBlob({ subject, html, attachments = [] }) {
  const boundary = `----=_Actracker_${Date.now()}`;
  const lines = [
    "MIME-Version: 1.0",
    "X-Unsent: 1",
    `Subject: ${subject}`,
    `Content-Type: multipart/mixed; boundary="${boundary}"`,
    "",
    `--${boundary}`,
    "Content-Type: text/html; charset=utf-8",
    "Content-Transfer-Encoding: 8bit",
    "",
    html,
  ];
  for (const att of attachments) {
    lines.push(
      `--${boundary}`,
      `Content-Type: ${att.contentType || "application/octet-stream"}; name="${att.fileName}"`,
      `Content-Disposition: attachment; filename="${att.fileName}"`,
      "Content-Transfer-Encoding: base64",
      "",
      att.base64 || ""
    );
  }
  lines.push(`--${boundary}--`);
  return new Blob([lines.join("\r\n")], { type: "message/rfc822" });
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

async function copyOutlookHtml(html) {
  try {
    if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([html.replace(/<[^>]+>/g, " ")], { type: "text/plain" }),
        }),
      ]);
      showSaveToast("Copied — paste into a new Outlook email (Ctrl+V)");
      return;
    }
  } catch { /* fallback */ }
  const ta = document.createElement("textarea");
  ta.value = html;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  ta.remove();
  showSaveToast("HTML copied to clipboard");
}

function openReportWindow(html, subject) {
  const w = window.open("", "_blank");
  if (!w) {
    window.alert("Pop-up blocked. Use Copy for Outlook or Download .eml instead.");
    return null;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.document.title = subject;
  return w;
}

function renderRtsTable(store, _fleetTamByMsn, onSave, onRerender, sortState) {
  const resolveRow = (row) => resolveStoreRow(store.mroPipeline || [], row, {
    matchKeys: [["msn", "reg"], ["msn"]],
  });
  const colDefs = [
    { key: "idx", label: "#" },
    { key: "msn", label: "MSN" },
    { key: "reg", label: "Reg" },
    { key: "mtxRequirement", label: "MTX requirement" },
    { key: "rtsStatus", label: "Status" },
    { key: "mtxStart", label: "MTX start" },
    { key: "mtxComplete", label: "MTX complete" },
    { key: "tatDays", label: "TAT [DY]" },
    { key: "mro", label: "MRO" },
    { key: "serviceabilityTable", label: RTS_FILING_SVC_HEADER, narrow: true },
    { key: "parts", label: "Parts / comments" },
  ];
  let rows = [...(store.mroPipeline || [])];
  const rtsSort = sortState?.key ? sortState : { key: "mtxStart", dir: "asc" };
  rows = sortRtsRows(rows, rtsSort);
  const body = rows.map((row, displayIdx) => {
    row.mtxStart = normalizeMtxDate(row.mtxStart);
    row.mtxComplete = normalizeMtxDate(row.mtxComplete);
    const tat = computeTatDays(row.mtxStart, row.mtxComplete);
    if (tat) row.tatDays = tat;
    const commitField = (field, val) => {
      const target = resolveRow(row);
      const next = field === "rtsStatus" ? normaliseRtsStatus(val) : val;
      if (field === "mtxStart" || field === "mtxComplete") {
        target[field] = cleanText(next);
        target.tatDays = computeTatDays(target.mtxStart, target.mtxComplete) || "";
      } else {
        target[field] = next;
      }
      stampRowChangedToday(target);
      onSave();
    };
    return el("tr", { class: "border-t border-gray-100", "data-rts-row-id": row._rowId || "" }, [
      el("td", { class: "px-2 py-1 text-xs text-gray-500", text: String(displayIdx + 1) }),
      clickEditCell({ value: row.msn, onCommit: (v) => commitField("msn", v) }),
      clickEditCell({ value: row.reg, onCommit: (v) => commitField("reg", v) }),
      clickEditCell({ value: row.mtxRequirement, onCommit: (v) => commitField("mtxRequirement", v) }),
      clickEditCell({
        value: row.rtsStatus || "scheduled",
        selectOptions: RTS_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
        renderDisplay: (v) => rtsStatusBadge(v),
        onCommit: (v) => commitField("rtsStatus", v),
      }),
      clickEditDateCell({ value: row.mtxStart, onCommit: (v) => commitField("mtxStart", v) }),
      clickEditDateCell({ value: row.mtxComplete, onCommit: (v) => commitField("mtxComplete", v) }),
      clickEditCell({ value: row.tatDays || tat, readOnly: true }),
      clickEditCell({ value: row.mro, onCommit: (v) => commitField("mro", v) }),
      clickEditCell({ value: row.serviceabilityTable, onCommit: (v) => commitField("serviceabilityTable", v) }),
      chronologyCell(el, rtsPartsTracker(resolveRow(row)), () => {
        const target = resolveRow(row);
        syncPartsFromHistory(target);
        stampRowChangedToday(target);
        onSave();
      }, { className: "px-2 py-1 min-w-[220px] align-top" }),
    ]);
  });
  return el("div", { class: "overflow-x-auto border border-gray-200 rounded-xl" }, [
    el("table", { class: "min-w-full text-left text-xs" }, [
      el("thead", { class: "bg-dae text-white uppercase" }, [
        el("tr", {}, colDefs.map((c) => sortableTh(c.label, c.key, sortState, onRerender, "", { narrow: c.narrow }))),
      ]),
      el("tbody", {}, body),
    ]),
    el("div", { class: "p-2 border-t border-gray-100" }, [
      el("button", {
        type: "button",
        class: "text-xs text-dae hover:underline",
        text: "+ Add row",
        onclick: () => {
          store.mroPipeline.push({
            _rowId: `rts-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            msn: "", reg: "", mtxRequirement: "", rtsStatus: "scheduled", mtxStart: "", mtxComplete: "",
            tatDays: "", mro: "", serviceabilityTable: "", parts: "", partsHistory: [],
          });
          onRerender();
        },
      }),
    ]),
  ]);
}

function renderEngineTable(store, onSave, onRerender, sortState) {
  const resolveRow = (row) => resolveStoreRow(store.engineTracker || [], row, { matchKeys: ["esn"] });
  const colDefs = [
    { key: "esn", label: "ESN" },
    { key: "status", label: "Status" },
    { key: "disposition", label: "Disposition" },
    { key: "location", label: "Location" },
    { key: "target", label: "Target" },
    { key: "cost", label: "Cost" },
    { key: "svc", label: "Svc", sortable: false },
    { key: "reason", label: "Reason / notes" },
  ];
  let rows = sortState?.key
    ? applyTableSort(
      (store.engineTracker || []).map((e) => { normalizeEngineTarget(e); return e; }),
      sortState,
      (r) => engineSortValue(r, sortState.key),
    )
    : sortEngineTracker(store.engineTracker || []);
  const body = rows.map((row) => {
    const commit = (field, val) => {
      const target = resolveRow(row);
      target[field] = val;
      if (field === "targetMode" || field === "targetDate" || field === "targetLegacy" || field === "target") {
        normalizeEngineTarget(target);
      }
      stampRowChangedToday(target);
      onSave();
    };
    const storeRow = resolveRow(row);
    const reasonTracker = engineReasonTracker(storeRow);
    return el("tr", { class: "border-t border-gray-100 align-top", "data-eng-row-id": row._rowId || "" }, [
      clickEditCell({ value: row.esn, onCommit: (v) => commit("esn", v) }),
      clickEditCell({
        value: row.azulStatus,
        selectOptions: [
          { value: "UNSERVICEABLE", label: "Unserviceable" },
          { value: "SERVICEABLE", label: "Serviceable" },
        ],
        renderDisplay: (v) => engineStatusBadge(v),
        onCommit: (v) => commit("azulStatus", v),
      }),
      clickEditCell({
        value: row.disposition,
        selectOptions: [
          { value: "", label: "—" },
          { value: "in_shop", label: "In shop" },
          { value: "in_storage", label: "In storage" },
        ],
        renderDisplay: (v) => engineDispositionBadge(v) || progressBadge("—", "gray"),
        onCommit: (v) => commit("disposition", v),
      }),
      clickEditCell({ value: row.location, onCommit: (v) => commit("location", v) }),
      engineTargetEditCell(row, onSave, resolveRow),
      clickEditCell({
        value: row.cost,
        className: "px-2 py-1 text-right tabular-nums",
        renderDisplay: (v) => el("span", { text: formatEngineCost(v) }),
        onCommit: (v) => commit("cost", normalizeEngineCostStorage(v)),
      }),
      el("td", { class: "px-2 py-1 text-center" }, [
        row.fromServiceability === "true" || row.fromServiceability === true
          ? el("span", { class: "text-dae font-bold", text: "●", title: "From serviceability table" })
          : el("span", { class: "text-gray-300", text: "—" }),
      ]),
      chronologyCell(el, reasonTracker, () => {
        syncReasonFromHistory(storeRow);
        onSave();
      }, { className: "px-2 py-1 min-w-[240px] align-top" }),
    ]);
  });
  return el("div", { class: "overflow-x-auto border border-gray-200 rounded-xl" }, [
    el("table", { class: "min-w-full text-left text-xs" }, [
      el("thead", { class: "bg-dae text-white uppercase" }, [
        el("tr", {}, colDefs.map((c) => (c.sortable === false ? el("th", { class: "px-2 py-2", text: c.label }) : sortableTh(c.label, c.key, sortState, onRerender)))),
      ]),
      el("tbody", {}, body),
      el("tfoot", { class: "bg-gray-50 border-t-2 border-gray-300" }, [
        el("tr", {}, [
          el("td", { class: "px-2 py-2 text-right font-semibold text-gray-700", colspan: "5", text: "Total" }),
          el("td", {
            class: "px-2 py-2 text-right font-semibold text-gray-900 tabular-nums",
            text: formatEngineCost(sumEngineTrackerCost(store.engineTracker)),
          }),
          el("td", { class: "px-2 py-2" }),
          el("td", { class: "px-2 py-2" }),
        ]),
      ]),
    ]),
    el("div", { class: "p-2 border-t border-gray-100" }, [
      el("button", {
        type: "button",
        class: "text-xs text-dae hover:underline",
        text: "+ Add engine",
        onclick: () => {
          store.engineTracker.push({
            _rowId: `eng-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            esn: "", azulStatus: "UNSERVICEABLE", reason: "", location: "", target: "TBD",
            targetMode: "tbd", targetDate: "", cost: "",
            fromServiceability: false, disposition: "", reasonHistory: [],
          });
          onSave();
          onRerender();
        },
      }),
    ]),
  ]);
}

function renderServiceabilityTable(store, onSave, sortState, onSort) {
  const resolveRow = (row) => resolveStoreRow(store.serviceability || [], row, { matchKeys: ["msn"] });
  let rows = [...(store.serviceability || [])];
  if (sortState?.key) {
    rows = applyTableSort(rows, sortState, (r) => serviceabilitySortValue(r, sortState.key));
  }
  const afPositions = AF_KEYS.map((_, i) => (AF_KEYS.length === 1 ? "single" : i === 0 ? "start" : i === AF_KEYS.length - 1 ? "end" : "mid"));
  const lgPositions = LG_KEYS.map((_, i) => (LG_KEYS.length === 1 ? "single" : i === 0 ? "start" : i === LG_KEYS.length - 1 ? "end" : "mid"));
  const propPositions = PROP_KEYS.map((_, i) => (PROP_KEYS.length === 1 ? "single" : i === 0 ? "start" : "end"));

  const suCell = (get, set, borderPos) =>
    clickEditCell({
      value: get(),
      selectOptions: [
        { value: "S", label: "S" },
        { value: "U", label: "U" },
      ],
      renderDisplay: (v) => {
        const u = String(v || "S").toUpperCase() === "U";
        return el("span", { class: u ? "font-bold text-gray-900" : "font-normal text-gray-900", text: u ? "U" : "S" });
      },
      onCommit: set,
      className: `px-1 py-1 text-center ${svcGroupBorder(borderPos)}`,
    });

  const body = rows.map((row, rowIdx) => {
    const stripe = rowIdx % 2 === 0 ? "bg-white" : "bg-gray-50";
    const commitMsn = (v) => { resolveRow(row).msn = v; onSave(); };
    const commitNested = (objKey, fieldKey, v) => {
      const target = resolveRow(row);
      if (!target[objKey]) target[objKey] = { esn: "", status: "S" };
      target[objKey][fieldKey] = v;
      onSave();
    };
    const commitComments = (v) => { resolveRow(row).comments = v; onSave(); };
    return el("tr", { class: `border-t border-gray-100 align-top ${stripe}`, "data-svc-row-id": row._rowId || "" }, [
      clickEditCell({
        value: row.msn,
        onCommit: commitMsn,
        className: `px-2 py-1 font-mono ${stripe}`,
      }),
      ...AF_KEYS.map((k, i) =>
        suCell(
          () => row.airframe?.[k] || "S",
          (v) => commitNested("airframe", k, v),
          afPositions[i]
        )
      ),
      ...LG_KEYS.map((k, i) =>
        suCell(
          () => row.landingGear?.[k] || "S",
          (v) => commitNested("landingGear", k, v),
          lgPositions[i]
        )
      ),
      ...PROP_KEYS.map((k, i) =>
        suCell(
          () => row.propellers?.[k] || "S",
          (v) => commitNested("propellers", k, v),
          propPositions[i]
        )
      ),
      clickEditCell({
        value: row.eng1?.esn,
        onCommit: (v) => commitNested("eng1", "esn", v),
        className: `px-1 py-1 ${svcGroupBorder("start")}`,
      }),
      suCell(
        () => row.eng1?.status || "S",
        (v) => commitNested("eng1", "status", v),
        "end"
      ),
      clickEditCell({
        value: row.eng2?.esn,
        onCommit: (v) => commitNested("eng2", "esn", v),
        className: `px-1 py-1 ${svcGroupBorder("start")}`,
      }),
      suCell(
        () => row.eng2?.status || "S",
        (v) => commitNested("eng2", "status", v),
        "end"
      ),
      clickEditCell({
        value: row.comments,
        multiline: true,
        onCommit: commitComments,
        className: `px-2 py-1 min-w-[180px] ${stripe}`,
      }),
    ]);
  });

  const groupTh = (label, colspan) =>
    el("th", {
      class: "px-2 py-2 text-center border-2 border-white/30",
      colspan,
      text: label,
    });

  const headRow1 = [
    sortableTh("MSN", "msn", sortState, onSort, "align-bottom", { rowspan: 2 }),
    groupTh("Airframe", AF_KEYS.length),
    groupTh("Landing gear", LG_KEYS.length),
    groupTh("Propellers", PROP_KEYS.length),
    groupTh("Eng 1", 2),
    groupTh("Eng 2", 2),
    sortableTh("Comments", "comments", sortState, onSort, "align-bottom", { rowspan: 2 }),
  ];
  const headRow2 = [
    ...AF_KEYS.map((k) => el("th", { class: "px-1 py-1 text-[10px] border-b border-white/20", text: k })),
    ...LG_KEYS.map((k) => el("th", { class: "px-1 py-1 text-[10px] border-b border-white/20", text: k })),
    ...PROP_KEYS.map((k) => el("th", { class: "px-1 py-1 text-[10px] border-b border-white/20", text: k })),
    el("th", { class: "px-1 py-1 text-[10px] border-b border-white/20", text: "ESN" }),
    el("th", { class: "px-1 py-1 text-[10px] border-b border-white/20", text: "S/U" }),
    el("th", { class: "px-1 py-1 text-[10px] border-b border-white/20", text: "ESN" }),
    el("th", { class: "px-1 py-1 text-[10px] border-b border-white/20", text: "S/U" }),
  ];

  return el("div", { class: "overflow-x-auto border border-gray-200 rounded-xl" }, [
    el("table", { class: "min-w-max text-left text-xs border-collapse" }, [
      el("thead", { class: "bg-dae text-white uppercase" }, [el("tr", {}, headRow1), el("tr", {}, headRow2)]),
      el("tbody", {}, body),
    ]),
  ]);
}

function buildProgressHeatmap(sortedRows) {
  const tiles = (sortedRows || []).map((r) => {
    const tam = normaliseTam(r.tam);
    const faUrl = r.flightawareUrl || flightawareAircraftUrl(r.reg);
    const modelEngine = [r.model, r.engine].filter((x) => x && x !== "—").join(" · ");
    return el("button", {
      type: "button",
      class: `text-left rounded-xl p-3 text-white shadow-sm hover:opacity-95 focus:ring-4 focus:ring-dae/20 ${tamTileClass(tam)}`,
      onclick: () => { if (faUrl) window.open(faUrl, "_blank", "noopener"); },
      title: `${normaliseTam(tam)} · ${modelEngine || "—"} · ${r.loc} · ${r.tog || (r.status === "AIRBORNE" ? "AIRBORNE" : "—")}`,
    }, [
      el("div", { class: "text-sm font-semibold", text: `MSN ${r.msn} · ${r.reg}` }),
      el("div", { class: "mt-1 text-xs opacity-95", text: modelEngine || "—" }),
      el("div", { class: "mt-0.5 text-xs opacity-90", text: `${normaliseTam(tam)} · ${r.tog || (r.status === "AIRBORNE" ? "AIRBORNE" : "—")}` }),
    ]);
  });
  return el("div", { class: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" }, tiles);
}

export function renderOperatorProgressPage(ctx, params) {
  el = ctx.el;
  cleanText = ctx.cleanText;
  normaliseTam = ctx.normaliseTam;
  htmlEscape = ctx.htmlEscape;
  formatMaterialsExportTimeEst = ctx.formatMaterialsExportTimeEst;
  tamTileClass = ctx.tamTileClass;
  flightawareAircraftUrl = ctx.flightawareAircraftUrl;
  tamBadge = ctx.tamBadge;
  const {
    state,
    isAdminSession,
    hasFleetScopeAccess,
    operatorNamesForSession,
    aircraftModelFamily,
    aircraftRowForTable,
    renderHeatmapStatusLegend,
    loadOperators,
    fetchJson,
    apiUrl,
    currentRouteParams,
    refreshRoute,
  } = ctx;

  if (!hasFleetScopeAccess()) {
    return el("div", { class: "p-8 text-center text-gray-600" }, [
      el("h1", { class: "text-xl font-semibold text-gray-900", text: "Operator progress" }),
      el("p", { class: "mt-2", text: "Fleet manager or administrator access required." }),
    ]);
  }

  const readUi = () => {
    const live = typeof currentRouteParams === "function" ? currentRouteParams() : params;
    return {
      operator: cleanText(live.get("operator")) || "",
      families: (cleanText(live.get("families")) || "").split(",").map((s) => cleanText(s)).filter(Boolean),
    };
  };

  const ui = readUi();
  const contentMount = document.getElementById("v2-content");
  const canReuseLiveHost =
    opProgressLiveHost &&
    opProgressLiveApi &&
    contentMount?.contains(opProgressLiveHost);

  if (canReuseLiveHost) {
    opProgressLiveApi.refreshCtx(ctx);
    opProgressLiveApi.syncFromRoute();
    return opProgressLiveHost;
  }

  if (opProgressLiveHost && !contentMount?.contains(opProgressLiveHost)) {
    opProgressLiveHost = null;
    opProgressLiveApi = null;
    opSurveillanceSection = null;
    opSurveillanceOperatorId = null;
  }

  const host = el("div", { "data-op-progress-page": "1" });
  const storeRef = {
    operator: ui.operator,
    data: ui.operator ? acquireStore(ui.operator, state.operators) : null,
    _uiFamilies: ui.families.slice(),
  };
  const saveStatus = { text: "", warn: false, loading: Boolean(ui.operator) };
  let operatorsLoadError = "";
  let operatorsFetching = !Array.isArray(state.operators);
  let serverSaveTimer = null;
  let hydrationGen = 0;
  let survGen = 0;
  const survState = { loading: Boolean(ui.operator), campaigns: [] };

  const refreshSurveillances = async () => {
    const opId = operatorIdForName(storeRef.operator, state.operators);
    if (!opId || !fetchJson || !apiUrl) {
      survState.loading = false;
      survState.campaigns = [];
      return;
    }
    const gen = ++survGen;
    survState.campaigns = await loadSurveillanceCampaigns(opId, fetchJson, apiUrl);
    if (gen !== survGen) return;
    survState.loading = false;
  };

  const formatSaveTime = (iso) => {
    if (!iso) return "";
    try {
      return new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
    } catch {
      return String(iso);
    }
  };

  let rerenderPending = false;
  let rerenderScheduled = false;

  const isEditingInPage = () => {
    const active = document.activeElement;
    if (active?.id === "op-progress-operator") return false;
    if (host.querySelector("[data-op-editing]")) return true;
    if (document.querySelector("body > [data-op-cell-menu]")) return true;
    if (active?.dataset?.opBlockRerender === "1") return true;
    return false;
  };

  const mountPageContent = () => {
    try {
      host.replaceChildren(buildPage());
    } catch (err) {
      console.error("Operator progress render failed", err);
      host.replaceChildren(el("div", { class: "p-8" }, [
        el("h1", { class: "text-xl font-semibold text-red-800", text: "Could not render operator progress" }),
        el("p", { class: "mt-2 text-sm text-gray-700", text: cleanText(err?.message) || String(err) }),
        el("button", {
          type: "button",
          class: "mt-4 px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90",
          text: "Retry",
          onclick: () => forceRerender(),
        }),
      ]));
    }
  };

  const scheduleRerender = () => {
    if (rerenderScheduled) return;
    rerenderScheduled = true;
    requestAnimationFrame(() => {
      rerenderScheduled = false;
      rerender();
    });
  };

  const forceRerender = () => {
    flushActiveEditor();
    document.querySelectorAll("body > [data-op-cell-menu]").forEach((node) => node.remove());
    rerenderPending = false;
    mountPageContent();
  };

  const deferRerender = () => {
    if (isEditingInPage()) {
      rerenderPending = true;
      return;
    }
    mountPageContent();
  };

  const rerender = () => {
    if (isEditingInPage()) {
      rerenderPending = true;
      return;
    }
    rerenderPending = false;
    mountPageContent();
  };

  const rerenderIfPending = () => {
    if (rerenderPending && !isEditingInPage()) {
      rerenderPending = false;
      mountPageContent();
    }
  };

  let saveStatusEl = null;
  const updateSaveStatusUi = () => {
    if (!saveStatusEl?.isConnected) return;
    saveStatusEl.textContent = saveStatus.loading
      ? "Loading saved data…"
      : saveStatus.text || "Edits save automatically to this browser and the server.";
    saveStatusEl.className = saveStatus.warn ? "text-xs text-amber-700 font-medium" : "text-xs text-gray-500";
  };

  let serverSaveQueued = false;

  const flushServerSaveKeepalive = () => {
    const opId = operatorIdForName(storeRef.operator, state.operators);
    if (!opId || !storeRef.data || !apiUrl) return;
    try {
      fetch(apiUrl(`/api/operator-progress/${opId}`), {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ data: storeRef.data }),
        keepalive: true,
      });
    } catch { /* ignore */ }
  };

  const flushServerSave = async () => {
    if (!storeRef.data || !fetchJson || !apiUrl) return;
    const opId = operatorIdForName(storeRef.operator, state.operators);
    if (!opId) {
      serverSaveQueued = true;
      saveStatus.text = "Saved in browser — waiting to sync (operator directory loading)…";
      saveStatus.warn = true;
      saveStatus.loading = false;
      updateSaveStatusUi();
      return;
    }
    serverSaveQueued = false;
    saveStatus.text = "Saving to server…";
    saveStatus.loading = true;
    updateSaveStatusUi();
    const resp = await saveStoreToServer(opId, storeRef.data, fetchJson, apiUrl);
    if (resp?.ok) {
      storeRef.data._serverUpdatedAt = resp.updatedAt || new Date().toISOString();
      storeRef.data._lastSavedAt = storeRef.data._serverUpdatedAt;
      saveStore(storeRef.operator, storeRef.data, state.operators, { touch: false });
      saveStatus.text = `Saved to server · ${formatSaveTime(resp.updatedAt)}`;
      saveStatus.warn = false;
      showSaveToast("Saved to server");
    } else {
      const errText = String(resp?.error || "");
      const hint = /404|not found/i.test(errText)
        ? " — server API may need deploy"
        : isRetriableServerError(errText)
          ? " — server was restarting; your edit is in this browser, retrying…"
          : "";
      saveStatus.text = `Saved in browser only — server sync failed${errText ? `: ${errText}` : ""}${hint}`;
      saveStatus.warn = true;
      serverSaveQueued = true;
    }
    saveStatus.loading = false;
    updateSaveStatusUi();
  };

  const scheduleServerSave = () => {
    serverSaveQueued = true;
    clearTimeout(serverSaveTimer);
    serverSaveTimer = setTimeout(() => flushServerSave(), 500);
  };

  const hydrateStoreFromServer = async () => {
    const op = storeRef.operator;
    if (!op || !storeRef.data) {
      saveStatus.loading = false;
      return;
    }
    const gen = ++hydrationGen;
    const opId = operatorIdForName(op, state.operators);
    if (!opId || !fetchJson || !apiUrl) {
      saveStatus.loading = false;
      return;
    }

    saveStatus.loading = true;
    updateSaveStatusUi();
    const serverResult = await loadStoreFromServer(opId, fetchJson, apiUrl);
    if (gen !== hydrationGen) return;

    if (serverResult?.error) {
      saveStatus.text = `Could not load from server: ${serverResult.error}`;
      saveStatus.warn = true;
      saveStatus.loading = false;
      updateSaveStatusUi();
      return;
    }

    const serverData = serverResult;
    if (serverData) {
      const local = storeRef.data;
      const merged = pickAuthoritativeStore(local, serverData);
      const before = merged.projects?.length || 0;
      const beforeActive = merged.activeProjectId;
      applyImportBundles(merged, op);
      const importAdded = (merged.projects?.length || 0) > before;
      const structureChanged = importAdded
        || beforeActive !== merged.activeProjectId
        || JSON.stringify(local?.projects) !== JSON.stringify(merged.projects)
        || JSON.stringify(local?.engineTracker) !== JSON.stringify(merged.engineTracker);
      if (serverData._serverUpdatedAt && storeRichness(serverData) >= storeRichness(merged)) {
        merged._serverUpdatedAt = serverData._serverUpdatedAt;
      }
      if (!hasUnsyncedLocalEdits(local) || merged === serverData) {
        merged._lastSavedAt = merged._serverUpdatedAt || merged._lastSavedAt;
      }
      storeRef.data = merged;
      sessionStoreCache.set(storageKey(op, state.operators), merged);
      saveStore(op, merged, state.operators, { touch: false });

      if ((hasUnsyncedLocalEdits(local) && merged === local) || importAdded) {
        await flushServerSave();
        if (structureChanged) queueMicrotask(() => forceRerender());
        saveStatus.loading = false;
        updateSaveStatusUi();
        return;
      }
      saveStatus.text = `Synced from server · ${formatSaveTime(serverData._serverUpdatedAt)} · ${storeRichness(merged)} data points`;
      saveStatus.warn = false;
      saveStatus.loading = false;
      updateSaveStatusUi();
      if (structureChanged) queueMicrotask(() => forceRerender());
      return;
    }
    if (hasUnsyncedLocalEdits(storeRef.data)) {
      await flushServerSave();
      return;
    }

    saveStatus.loading = false;
    updateSaveStatusUi();
    if (serverSaveQueued) scheduleServerSave();
  };

  const reloadStoreAfterOperators = () => {
    const op = storeRef.operator;
    if (!op) return;
    const key = storageKey(op, state.operators);
    const cached = sessionStoreCache.get(key);
    const disk = loadStoreFromDisk(op, state.operators, { allowSeed: true });
    storeRef.data = pickNewerStore(cached, disk);
    applyImportBundles(storeRef.data, op);
    sessionStoreCache.set(key, storeRef.data);
    saveStore(op, storeRef.data, state.operators);
    hydrateStoreFromServer();
    refreshSurveillances();
  };

  const persistQuiet = () => {
    if (storeRef.operator && storeRef.data) {
      saveStore(storeRef.operator, storeRef.data, state.operators);
      scheduleServerSave();
    }
    rerenderIfPending();
  };

  const persistWithToast = () => {
    persistQuiet();
  };

  const persistFleetTableEdit = () => {
    persistWithToast();
    deferRerender();
  };

  if (opProgressUnloadAbort) opProgressUnloadAbort.abort();
  opProgressUnloadAbort = new AbortController();
  window.addEventListener("beforeunload", () => {
    flushActiveEditor();
    if (storeRef.operator && storeRef.data) {
      saveStore(storeRef.operator, storeRef.data, state.operators);
      clearTimeout(serverSaveTimer);
      flushServerSaveKeepalive();
    }
  }, { signal: opProgressUnloadAbort.signal });

  const reloadOperatorsDirectory = () => {
    operatorsFetching = true;
    operatorsLoadError = "";
    return loadOperators()
      .then(() => {
        operatorsFetching = false;
        if (storeRef.operator) reloadStoreAfterOperators();
        else {
          const fmNames = operatorNamesForSession
            ? operatorNamesForSession(state.operators || [], state.aircraft || [])
            : [];
          if (!isAdminSession() && fmNames.length === 1 && !readUi().operator) {
            navigateParams((p) => p.set("operator", fmNames[0]));
          }
        }
        scheduleRerender();
      })
      .catch((err) => {
        operatorsFetching = false;
        operatorsLoadError = cleanText(err?.message) || String(err);
        scheduleRerender();
      });
  };

  const syncFromRoute = () => {
    const live = readUi();
    if (!isAdminSession() && operatorNamesForSession && live.operator) {
      const allowed = operatorNamesForSession(state.operators || [], state.aircraft || []);
      if (allowed.length && !allowed.includes(live.operator)) {
        queueMicrotask(() => navigateParams((p) => {
          p.set("operator", allowed[0]);
        }));
        return;
      }
    }
    const famKey = (live.families || []).slice().sort().join(",");
    const prevFamKey = (storeRef._uiFamilies || []).slice().sort().join(",");
    const opChanged = live.operator !== storeRef.operator;
    const famChanged = famKey !== prevFamKey;
    if (!opChanged && !famChanged) return;
    if (opChanged) {
      hydrationGen += 1;
      survGen += 1;
      opSurveillanceSection = null;
      opSurveillanceOperatorId = null;
      if (storeRef.operator && storeRef.data) {
        saveStore(storeRef.operator, storeRef.data, state.operators);
      }
      storeRef.operator = live.operator || "";
      storeRef.data = live.operator ? acquireStore(live.operator, state.operators) : null;
      storeRef._uiFamilies = live.families.slice();
      saveStatus.loading = Boolean(live.operator);
      if (live.operator) {
        applyImportBundles(storeRef.data, live.operator);
        saveStore(live.operator, storeRef.data, state.operators);
        hydrateStoreFromServer();
        refreshSurveillances();
      } else {
        saveStatus.loading = false;
        survState.loading = false;
        survState.campaigns = [];
      }
    } else {
      storeRef._uiFamilies = live.families.slice();
    }
    queueMicrotask(() => forceRerender());
  };

  const navigateParams = (mutator) => {
    flushActiveEditor();
    if (storeRef.operator && storeRef.data) {
      saveStore(storeRef.operator, storeRef.data, state.operators);
    }
    const live = typeof currentRouteParams === "function" ? currentRouteParams() : params;
    const p = new URLSearchParams(live.toString());
    mutator(p);
    const nextHash = `#/operator-progress?${p.toString()}`;
    try {
      history.replaceState(null, "", nextHash);
    } catch {
      window.location.hash = nextHash;
      return;
    }
    syncFromRoute();
  };

  if (!Array.isArray(state.operators)) {
    reloadOperatorsDirectory();
  } else if (ui.operator) {
    applyImportBundles(storeRef.data, ui.operator);
    hydrateStoreFromServer();
    refreshSurveillances();
  } else {
    saveStatus.loading = false;
    const fmNames = operatorNamesForSession
      ? operatorNamesForSession(state.operators || [], state.aircraft || [])
      : [];
    if (!isAdminSession() && fmNames.length === 1) {
      queueMicrotask(() => navigateParams((p) => p.set("operator", fmNames[0])));
    }
  }

  const sortState = {
    rts: { key: "mtxStart", dir: "asc" },
    engine: { key: null, dir: "asc" },
    serviceability: { key: null, dir: "asc" },
    ground: { key: null, dir: "asc" },
    fleet: { key: null, dir: "asc" },
  };

  function buildPage() {
    const ui = readUi();
    const operators = Array.isArray(state.operators) ? state.operators : [];
    const scopedOperators = operatorNamesForSession
      ? operatorNamesForSession(operators, state.aircraft || [])
      : operatorDirectoryNames(operators);
    const opNames = scopedOperators;
    const operatorsLoading = operatorsFetching || !Array.isArray(state.operators);
    const fleetForOp = ui.operator
      ? (state.aircraft || []).filter((a) => aircraftMatchesOperator(a, ui.operator, state.operators))
      : [];
    const familyOptions = Array.from(new Set(fleetForOp.map((a) => aircraftModelFamily(a)).filter((f) => f && f !== "—"))).sort();
    const selectedFamilies = ui.families.length ? ui.families : familyOptions;
    const fleetRows = ui.operator ? collectFleetRows(ctx, ui.operator, selectedFamilies) : [];
    const fleetTamByMsn = tamByMsn(fleetRows);
    const weekMeta = reportWeekMeta();
    const subjectPreview = ui.operator ? reportSubject(ui.operator) : "—";

    const makeReport = () => {
      flushActiveEditor();
      persistQuiet();
      const reportCtx = buildReportContext(ctx, { operator: ui.operator, modelFamilies: selectedFamilies, store: storeRef.data });
      enrichReportSorting(reportCtx, storeRef.data, sortState);
      reportCtx.openSurveillances = survState.campaigns.map((c) => c.summary);
      reportCtx.surveillanceCampaigns = survState.campaigns;
      reportCtx.activeProject = activeProject(storeRef.data, storeRef.data?.activeProjectId);
      return reportCtx;
    };

    const runExport = (buildFn) => {
      if (!ui.operator) return;
      openExportSectionsDialog((sections) => {
        flushActiveEditor();
        persistQuiet();
        const reportCtx = makeReport();
        buildFn(reportCtx, sections);
      }, defaultEmailExportFlags());
    };

    const actionBar = el("div", { class: "flex flex-wrap gap-2 mt-4" }, [
      el("button", {
        type: "button",
        class: "inline-flex px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 disabled:opacity-40",
        text: "Preview report",
        disabled: !ui.operator,
        onclick: () => runExport((reportCtx, sections) => openReportWindow(buildOutlookHtml(reportCtx, sections), reportCtx.subject)),
      }),
      el("button", {
        type: "button",
        class: "inline-flex px-4 py-2 text-sm font-medium text-dae bg-white border border-dae/40 rounded-lg hover:bg-red-50 disabled:opacity-40",
        text: "New Outlook email (.eml)",
        disabled: !ui.operator,
        onclick: () =>
          runExport((reportCtx, sections) => {
            const html = buildOutlookHtml(reportCtx, sections);
            const blob = buildEmlBlob({ subject: reportCtx.subject, html });
            downloadBlob(blob, `${ui.operator.replace(/[^\w.-]+/g, "_")}_Progress_WK${reportCtx.weekMeta.week}-${reportCtx.weekMeta.year}.eml`);
          }),
      }),
      el("button", {
        type: "button",
        class: "inline-flex px-4 py-2 text-sm font-medium text-dae bg-white border border-dae/40 rounded-lg hover:bg-red-50 disabled:opacity-40",
        text: "Copy for Outlook",
        disabled: !ui.operator,
        onclick: () => runExport(async (reportCtx, sections) => copyOutlookHtml(buildOutlookHtml(reportCtx, sections))),
      }),
      el("button", {
        type: "button",
        class: "inline-flex px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40",
        text: "Download HTML",
        disabled: !ui.operator,
        onclick: () =>
          runExport((reportCtx, sections) => {
            downloadBlob(
              new Blob([buildOutlookHtml(reportCtx, sections)], { type: "text/html;charset=utf-8" }),
              `${ui.operator.replace(/[^\w.-]+/g, "_")}_Progress.html`
            );
          }),
      }),
    ]);

    const widgets = storeRef.data ? ensureVisibleWidgets(storeRef.data) : {};
    if (storeRef.data?._pendingAutoEnablePersist) {
      storeRef.data._pendingAutoEnablePersist = false;
      persistQuiet();
    }
    const widgetOn = (id) => widgets[id] === true;

    const widgetsBtn = ui.operator && storeRef.data
      ? el("button", {
          type: "button",
          class: "px-4 py-2 text-sm font-medium text-dae bg-white border border-dae/40 rounded-lg hover:bg-red-50 shrink-0",
          text: "Widgets",
          onclick: () => {
            openWidgetsDialog({
              el,
              sections: EXPORT_SECTIONS,
              widgets,
              onConfigured: () => {
                if (storeRef.data) storeRef.data._widgetsUserConfigured = true;
              },
              onChange: () => {
                persistQuiet();
                // Persist widget toggles immediately so a refresh does not revert them.
                clearTimeout(serverSaveTimer);
                flushServerSave();
                forceRerender();
              },
            });
          },
        })
      : null;

    const summarySection =
      fleetRows.length && storeRef.data && (widgetOn("fleetSummary") || widgetOn("enginesSummary") || widgetOn("ground"))
        ? (() => {
            const reportCtx = makeReport();
            const { tamSummary, total, engineSummary, ground } = reportCtx;
            const engTotal = engineSummary.total || 1;
            const tamCards = tamSummary.map(({ tam, count }) =>
              el("div", { class: "bg-white border border-gray-200 rounded-xl p-4 border-t-[3px] border-t-dae" }, [
                el("div", { class: "text-2xl font-bold text-gray-900", text: String(count) }),
                el("div", { class: "text-xs uppercase text-gray-500 mt-1", text: `${tam} · ${pct(count, total)}` }),
              ])
            );
            const engineCards = [
              el("div", { class: "bg-white border border-gray-200 rounded-xl p-4 border-t-[3px] border-t-dae" }, [
                el("div", { class: "text-2xl font-bold text-gray-900", text: String(engineSummary.total) }),
                el("div", { class: "text-xs uppercase text-gray-500 mt-2 leading-snug", text: ENGINES_UNSERVICEABLE_FILING_NOTE.toUpperCase() }),
                el("div", { class: "text-xs uppercase text-gray-500 mt-1", text: "Total engines · 100.0%" }),
              ]),
              el("div", { class: "bg-white border border-gray-200 rounded-xl p-4 border-t-[3px] border-t-dae" }, [
                el("div", { class: "text-2xl font-bold text-gray-900", text: String(engineSummary.serviceable) }),
                el("div", { class: "text-xs uppercase text-gray-500 mt-1", text: `Now serviceable · ${pct(engineSummary.serviceable, engTotal)}` }),
              ]),
              el("div", { class: "bg-white border border-gray-200 rounded-xl p-4 border-t-[3px] border-t-dae" }, [
                el("div", { class: "text-2xl font-bold text-gray-900", text: String(engineSummary.unserviceable) }),
                el("div", { class: "text-xs uppercase text-gray-500 mt-1", text: "Still unserviceable" }),
              ]),
            ];
            const modelScope = formatModelScopeLabel(selectedFamilies);
            const blocks = [];
            if (widgetOn("fleetSummary")) {
              blocks.push(
                sectionHeading(exportSectionLabel("fleetSummary"), { subtitle: `Models in scope: ${modelScope}` }),
                el("div", { class: "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" }, [
                  el("div", { class: "bg-white border border-gray-200 rounded-xl p-4 border-t-[3px] border-t-dae" }, [
                    el("div", { class: "text-2xl font-bold text-gray-900", text: String(total) }),
                    el("div", { class: "text-xs uppercase text-gray-500 mt-1", text: "Aircraft in portfolio" }),
                  ]),
                  ...tamCards,
                ])
              );
            }
            if (widgetOn("enginesSummary")) {
              blocks.push(
                sectionHeading(exportSectionLabel("enginesSummary")),
                el("div", { class: "grid grid-cols-2 md:grid-cols-3 gap-3" }, engineCards)
              );
            }
            if (widgetOn("ground")) {
              blocks.push(sectionHeading(exportSectionLabel("ground")), renderGroundHistogramTable(ground, sortState.ground, rerender));
            }
            if (!blocks.length) return null;
            return el("div", { class: "mt-6 space-y-4" }, blocks);
          })()
        : null;

    const fleetColDefs = [
      { key: "msn", label: "MSN" },
      { key: "reg", label: "Reg" },
      { key: "model", label: "Model" },
      { key: "tam", label: "Status" },
      { key: "tog", label: "Time on ground" },
      { key: "loc", label: "Location" },
    ];
    let displayFleetRows = [...fleetRows];
    if (sortState.fleet?.key) {
      displayFleetRows = applyTableSort(displayFleetRows, sortState.fleet, (r) => fleetRowSortValue(r, sortState.fleet.key));
    }

    const fleetSnapshotTable =
      fleetRows.length > 0 && widgetOn("fleetSnapshot")
        ? el("div", { class: "mt-8" }, [
            sectionHeading(exportSectionLabel("fleetSnapshot")),
            el("div", { class: "overflow-x-auto border border-gray-200 rounded-xl" }, [
              el("table", { class: "min-w-full text-left text-xs" }, [
                el("thead", { class: "bg-dae text-white uppercase" }, [
                  el("tr", {}, fleetColDefs.map((c) => sortableTh(c.label, c.key, sortState.fleet, rerender))),
                ]),
                el("tbody", {}, displayFleetRows.map((r, rowIdx) =>
                  el("tr", { class: `border-t border-gray-100 ${rowIdx % 2 === 0 ? "bg-white" : "bg-gray-50"}` }, [
                    el("td", { class: "px-2 py-1 font-mono", text: r.msn }),
                    el("td", { class: "px-2 py-1", text: r.reg }),
                    el("td", { class: "px-2 py-1", text: r.model }),
                    el("td", { class: "px-2 py-1" }, [tamBadge ? tamBadge(r.tam) : el("span", { text: normaliseTam(r.tam) })]),
                    el("td", { class: "px-2 py-1 font-mono", text: r.tog || (r.status === "AIRBORNE" ? "AIRBORNE" : "—") }),
                    el("td", { class: "px-2 py-1", text: r.loc }),
                  ])
                )),
              ]),
            ]),
          ])
        : null;

    const heatmapSection =
      fleetRows.length > 0 && widgetOn("heatmap")
        ? el("div", { class: "mt-8 bg-white border border-gray-200 rounded-xl p-4" }, [
            sectionHeading(exportSectionLabel("heatmap")),
            renderHeatmapStatusLegend(),
            buildProgressHeatmap(fleetRows),
          ])
        : null;

    const projectsEnabled = anyProjectWidgetOn(widgets);
    if (projectsEnabled && storeRef.data && anyProjectSectionOn(widgets)) {
      if (!Array.isArray(storeRef.data.projects)) storeRef.data.projects = [];
      if (!storeRef.data.projects.length) {
        const p = createBlankProject();
        p.title = ui.operator ? `${ui.operator} project` : "New project";
        storeRef.data.projects.push(p);
        storeRef.data.activeProjectId = p.id;
        persistQuiet();
        scheduleServerSave();
      }
    }
    const projectSection =
      storeRef.data && ui.operator && projectsEnabled
        ? renderProjectWidgets({
            el,
            cleanText,
            store: storeRef.data,
            project: activeProject(storeRef.data, storeRef.data?.activeProjectId),
            setActiveProjectId: (id) => { if (storeRef.data) storeRef.data.activeProjectId = id; },
            persist: persistQuiet,
            rerender: deferRerender,
            forceRerender,
            sectionHeading,
            progressBadge,
            clickEditCell,
            clickEditDateCell,
            sectionVisible: (id) => widgets[id] === true,
            operatorId: operatorIdForName(ui.operator, state.operators),
            fetchJson,
            apiUrl,
          })
        : null;

    const surveillanceOpId = operatorIdForName(ui.operator, state.operators);
    let surveillanceSection = null;
    if (ui.operator && widgetOn("surveillances")) {
      if (opSurveillanceSection && opSurveillanceOperatorId === surveillanceOpId && opSurveillanceSection.isConnected) {
        surveillanceSection = opSurveillanceSection;
      } else {
        opSurveillanceOperatorId = surveillanceOpId;
        opSurveillanceLoadGen += 1;
        opSurveillanceSection = renderOperatorSurveillanceEmbed(
          { el, fetchJson, apiUrl, cleanText, state },
          {
            operatorId: surveillanceOpId,
            operatorName: ui.operator,
            sectionHeading,
          },
        );
        surveillanceSection = opSurveillanceSection;
      }
    } else {
      opSurveillanceSection = null;
      opSurveillanceOperatorId = null;
    }

    const addProjectBtn = projectsEnabled && storeRef.data
      ? el("button", {
          type: "button",
          class: "px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 shrink-0",
          text: "+ New project",
          onclick: () => {
            const p = createBlankProject();
            p._focusTitle = true;
            storeRef.data.projects.push(p);
            storeRef.data.activeProjectId = p.id;
            persistQuiet();
            scheduleServerSave();
            forceRerender();
          },
        })
      : null;

    const headerActions = el("div", { class: "flex flex-wrap gap-2 shrink-0" }, [widgetsBtn, addProjectBtn].filter(Boolean));

    const fleetTableBlocks = [];
    if (storeRef.data && ui.operator) {
      if (widgetOn("rts")) {
        fleetTableBlocks.push(el("section", {}, [
          sectionHeading(exportSectionLabel("rts")),
          renderRtsTable(storeRef.data, fleetTamByMsn, persistFleetTableEdit, rerender, sortState.rts),
        ]));
      }
      if (widgetOn("serviceability")) {
        fleetTableBlocks.push(el("section", {}, [
          sectionHeading(exportSectionLabel("serviceability")),
          renderServiceabilityTable(storeRef.data, persistFleetTableEdit, sortState.serviceability, rerender),
        ]));
      }
      if (widgetOn("engineTracker")) {
        fleetTableBlocks.push(el("section", {}, [
          sectionHeading(exportSectionLabel("engineTracker")),
          renderEngineTable(storeRef.data, persistFleetTableEdit, rerender, sortState.engine),
        ]));
      }
    }
    const fleetTables = fleetTableBlocks.length
      ? el("div", { class: "space-y-8 mt-8" }, fleetTableBlocks)
      : null;

    return el("div", {}, [
      el("div", { class: "mb-4 flex flex-wrap items-start justify-between gap-4" }, [
        el("div", {}, [
          el("h1", { class: "text-2xl font-bold text-gray-800", text: "Operator progress" }),
          el("p", { class: "mt-1 text-xs text-gray-400", text: `Subject: ${subjectPreview} · ISO week ${weekMeta.week}-${weekMeta.year} (Dubai)` }),
        ]),
        headerActions,
      ]),
      el("div", { class: "bg-white border border-gray-200 rounded-xl p-4 space-y-4 mb-6" }, [
        el("div", { class: "flex flex-wrap items-end gap-4" }, [
          buildOperatorPicker({
            opNames,
            selectedValue: ui.operator,
            loading: operatorsLoading,
            loadError: operatorsLoadError,
            onPick: (name) => navigateParams((p) => {
              if (name) p.set("operator", name);
              else p.delete("operator");
            }),
            onRetry: () => {
              state.operators = null;
              reloadOperatorsDirectory();
            },
          }),
          el("div", { class: "flex-1 min-w-[200px]" }, [
            el("div", { class: "text-xs font-medium text-gray-600 mb-1", text: "Aircraft types" }),
            familyOptions.length
              ? el("div", { class: "flex flex-wrap gap-3" }, familyOptions.map((fam) => {
                  const checked = selectedFamilies.includes(fam);
                  return el("label", { class: "inline-flex items-center gap-2 text-sm text-gray-700" }, [
                    el("input", {
                      type: "checkbox",
                      checked,
                      onchange: (ev) => {
                        const next = new Set(selectedFamilies);
                        if (ev.target.checked) next.add(fam);
                        else next.delete(fam);
                        navigateParams((p) => {
                          const v = Array.from(next).join(",");
                          if (v) p.set("families", v);
                          else p.delete("families");
                        });
                      },
                    }),
                    el("span", { text: fam }),
                  ]);
                }))
              : el("span", { class: "text-sm text-gray-400", text: "No aircraft for operator" }),
          ]),
        ]),
        actionBar,
        el("p", { class: "text-xs text-gray-500", text: "For Outlook on Windows: use New Outlook email (.eml) or Copy for Outlook, then paste into a new message — formatting survives forward/reply." }),
        (() => {
          saveStatusEl = el("p", {
            class: saveStatus.warn ? "text-xs text-amber-700 font-medium" : "text-xs text-gray-500",
            text: saveStatus.loading
              ? "Loading saved data…"
              : saveStatus.text || "Edits save automatically to this browser and the server.",
          });
          return saveStatusEl;
        })(),
      ]),
      // Letter order on screen: A–H fleet widgets, then I surveillances, then projects (J–N; L last among those).
      !ui.operator
        ? el("p", { class: "mt-4 text-sm text-gray-500", text: "Select an operator above to load widgets and data." })
        : null,
      summarySection,
      fleetTables,
      fleetSnapshotTable,
      heatmapSection,
      surveillanceSection,
      projectsEnabled && ui.operator
        ? el("div", { class: "mt-6" }, [projectSection])
        : null,
    ]);
  }

  try {
    mountPageContent();
  } catch (err) {
    host.appendChild(el("div", { class: "p-8" }, [
      el("h1", { class: "text-xl font-semibold text-red-800", text: "Operator progress failed to load" }),
      el("p", { class: "mt-2 text-sm text-gray-700", text: cleanText(err?.message) || String(err) }),
      el("button", {
        type: "button",
        class: "mt-4 px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90",
        text: "Reload page",
        onclick: () => window.location.reload(),
      }),
    ]));
  }
  opProgressLiveHost = host;
  opProgressLiveApi = { syncFromRoute, refreshCtx: () => {} };
  return host;
}
